import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertCircle,
  Calendar,
  ChevronDown,
  ChevronRight,
  Download,
  Dumbbell,
  FileText,
  Loader2,
  Mountain,
  Play,
  RefreshCcw,
  Settings,
  Target,
  TrendingUp,
  User
} from 'lucide-react'

import { cn } from '../utils/cn'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Button } from '../components/ui/button'
import { MeasurementField } from '../components/ui/measurement'
import { Tooltip } from '../components/ui/tooltip'
import {
  CodifiedPlanRulesPane,
  CODIFIED_PLAN_RULES_BY_ID
} from '../components/plan-algorithm/CodifiedPlanRulesPane'
import { PlanConfigFormFields } from '../components/plan-algorithm/PlanConfigFormFields'
import { useMeasurements } from '../hooks/useMeasurements'
import {
  formatMeasurement,
  unitLabelFor,
  type MeasurementKind,
  type MeasurementUnit
} from '../lib/measurements'
import {
  testPlanGeneration,
  getActiveConfig,
  withPlanConfigDefaults,
  type GoalType,
  type TestPlanGenerationResponse,
  type EnduranceFrequency,
  type HikingFrequency,
  type TrueElevationWorkoutAccess,
  type EquipmentAccessOption,
  type HikeConcern,
  type PlanGenerationConfigDto,
  type PlanGenerationTraceEntryDto,
  type PlanTargetComparisonDto,
  type PlanTargetMultiplierDiagnosticsDto,
  type PlanTargetWaterfallDto,
  type MockUserProfileDto,
  type AgeRange,
  type ResolvedPackWeightDto
} from '../lib/api/planGeneration'
import { getPopularHikes, type PopularHike } from '../lib/api/popularHikes'
import { getTourOperators, type TourOperator } from '../lib/api/tourOperators'
import { getFitnessHikeTargets, type FitnessHikeTarget } from '../lib/api/fitnessHikeTargets'
import { formatHikeDifficulty, parseHikeDifficulty } from '../lib/hikeDifficulty'
import { formatWorkoutExerciseDetails } from '../lib/workoutExerciseDetails'

// Mock user profile state
type MockProfile = {
  enduranceFrequency: EnduranceFrequency
  hikingFrequency: HikingFrequency
  trueElevationAccess: TrueElevationWorkoutAccess[]
  equipmentAccess: EquipmentAccessOption[]
  concerns: HikeConcern[]
  preferredDays: string[]
  ageRange: AgeRange
  measurementUnit: MeasurementUnit
}

type GeneralFitnessLevel = '1' | '2' | '3' | '4' | '5'

// Goal input state
type GoalInputs = {
  goalType: GoalType
  popularHikeId: string
  tourOperatorId: string
  tourId: string
  generalFitnessLevel: GeneralFitnessLevel
  customDistanceKm: number
  customElevationMeters: number
  customDays: number
}

// Plan parameters
type PlanParams = {
  hikeDateEnabled: boolean
  hikeDate: string
  durationWeeksEnabled: boolean
  durationWeeks: number
  packWeightEnabled: boolean
  packWeightKg: number
}

const defaultProfile: MockProfile = {
  enduranceFrequency: 'TwoThreeDays',
  hikingFrequency: 'OneThreeDaysMonth',
  trueElevationAccess: ['Stairs', 'LocalHikesOrTrails'],
  equipmentAccess: ['TreadmillWithIncline', 'StairMachine'],
  concerns: [],
  preferredDays: ['Tuesday', 'Thursday', 'Saturday'],
  ageRange: 'Range30To39',
  measurementUnit: 'Metric'
}

// Measurement helpers bound to a specific unit (not the auth-account unit).
// The tester drives both generation rounding AND display off the mock profile's
// unit so the numbers shown are clean in whichever unit is being tested.
export const makeMeasurementHelpers = (unit: MeasurementUnit) => ({
  unit,
  isImperial: unit === 'Imperial',
  unitLabel: (kind: MeasurementKind) => unitLabelFor(kind, unit),
  format: (
    valueMetric: number | null | undefined,
    kind: MeasurementKind,
    options?: Parameters<typeof formatMeasurement>[3]
  ) => formatMeasurement(valueMetric, kind, unit, options)
})

type MeasurementHelpers = ReturnType<typeof makeMeasurementHelpers>

const defaultGoalInputs: GoalInputs = {
  goalType: 'PopularHike',
  popularHikeId: '',
  tourOperatorId: '',
  tourId: '',
  generalFitnessLevel: '2',
  customDistanceKm: 80,
  customElevationMeters: 3000,
  customDays: 5
}

const defaultPlanParams: PlanParams = {
  hikeDateEnabled: true,
  hikeDate: new Date(Date.now() + 12 * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 12 weeks from now
  durationWeeksEnabled: false,
  durationWeeks: 12,
  packWeightEnabled: false,
  packWeightKg: 9
}

const fallbackFitnessTargetOptions: Array<{ level: GeneralFitnessLevel; label: string }> = [
  { level: '1', label: 'Level 1 - Couch to Trail - Yosemite Valley Floor Loop' },
  { level: '2', label: 'Level 2 - Easy - Appalachian Trail: Shenandoah Shortened Central Section' },
  { level: '3', label: 'Level 3 - Moderate - Otter Trail' },
  { level: '4', label: 'Level 4 - Hard - W Trek: Torres del Paine' },
  { level: '5', label: 'Level 5 - Strenuous - Tour du Mont Blanc: Classic Circuit' }
]

const isGeneralFitnessLevel = (value: string): value is GeneralFitnessLevel =>
  value === '1' || value === '2' || value === '3' || value === '4' || value === '5'

const enduranceOptions: { value: EnduranceFrequency; label: string }[] = [
  { value: 'FourPlusDays', label: '4+ days per week, most weeks — L4' },
  { value: 'TwoThreeDays', label: '2-3 days per week, most weeks — L3' },
  { value: 'OneDayOrLess', label: '1 day per week or less — L2' },
  { value: 'Occasionally', label: 'Occasionally, with long gaps — L1' },
  { value: 'NotAtAll', label: "I don't train at all — L1" }
]

const hikingOptions: { value: HikingFrequency; label: string }[] = [
  { value: 'ThreePlusDays', label: '3+ days per week — L4' },
  { value: 'OneTwoDays', label: '1-2 days per week — L3' },
  { value: 'OneThreeDaysMonth', label: '1-3 days per month — L2' },
  { value: 'FewTimes', label: 'A few times — L1' },
  { value: 'NotAtAll', label: "I don't hike at all — L1" }
]

const trueElevationOptions: { value: TrueElevationWorkoutAccess; label: string }[] = [
  { value: 'Stairs', label: 'Stairs' },
  { value: 'StepUps', label: 'Step-ups' },
  { value: 'LocalHikesOrTrails', label: 'Local trails' },
  { value: 'Hills', label: 'Hills' }
]

const equipmentOptions: { value: EquipmentAccessOption; label: string }[] = [
  { value: 'TreadmillWithIncline', label: 'Treadmill (incline)' },
  { value: 'StairMachine', label: 'Stair machine' }
]

const concernOptions: { value: HikeConcern; label: string }[] = [
  { value: 'Endurance', label: 'Endurance' },
  { value: 'Joints', label: 'Joint/knee issues' },
  { value: 'Altitude', label: 'Altitude' },
  { value: 'Strength', label: 'Strength' }
]

const ageRangeOptions: { value: AgeRange; label: string }[] = [
  { value: 'Range18To29', label: '18-29' },
  { value: 'Range30To39', label: '30-39' },
  { value: 'Range40To49', label: '40-49' },
  { value: 'Range50Plus', label: '50+' }
]

const goalTypeLabels: Record<GoalType, string> = {
  PopularHike: 'Popular Hike',
  TourOperatorHike: 'Tour Operator Hike',
  GeneralFitness: 'Hiking Fitness',
  CustomHike: 'Custom Hike'
}

const dayOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

const parseCalendarDate = (date: string) => {
  const [datePart] = date.split('T')
  const [year, month, day] = datePart.split('-').map(Number)
  if (!year || !month || !day) return new Date(date)
  return new Date(year, month - 1, day)
}

const formatTraceDate = (date: string) =>
  parseCalendarDate(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })

const formatTraceWeekday = (date: string) =>
  parseCalendarDate(date).toLocaleDateString(undefined, { weekday: 'long' })

const formatTraceContextMetrics = (
  context: PlanGenerationTraceEntryDto['contexts'][number],
  measurements: MeasurementHelpers
) => {
  const metrics: string[] = []
  if (context.distanceKm && context.distanceKm > 0) {
    metrics.push(measurements.format(context.distanceKm, 'distance'))
  }
  if (context.elevationGainMeters && context.elevationGainMeters > 0) {
    metrics.push(measurements.format(context.elevationGainMeters, 'elevation'))
  }
  if (context.packWeightKg && context.packWeightKg > 0) {
    metrics.push(`${measurements.format(context.packWeightKg, 'weight')} pack`)
  }
  return metrics.join(' / ')
}

const varianceClass = (pct: number) =>
  pct < -5 ? 'text-red-500' : pct > 5 ? 'text-amber-500' : 'text-green-500'

// Shared so the variance % reads the same wherever it appears (toolbar + accuracy rows).
const VARIANCE_HINT =
  'How far the generated plan total deviates from the goal target. Negative = under target, positive = over.'

// Compact metric used in the pinned results toolbar — glanceable while scrolling deep into a plan
export const ToolbarStat = ({
  label,
  value,
  variance,
  target,
  hint
}: {
  label: string
  value: string
  variance?: number
  target?: string
  hint?: string
}) => (
  <Tooltip as="div" content={hint} className="flex flex-col leading-tight">
    <span className="font-mono text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
      {label}
    </span>
    <span className="flex items-baseline gap-1.5 whitespace-nowrap text-sm text-foreground">
      <span className="font-bold">
        {value}
        {typeof variance === 'number' && (
          <span className={`ml-1 text-xs font-semibold ${varianceClass(variance)}`}>
            {variance > 0 ? '+' : ''}{variance.toFixed(0)}%
          </span>
        )}
      </span>
      {target && (
        <span className="text-[10px] font-medium text-muted-foreground">
          target {target}
        </span>
      )}
    </span>
  </Tooltip>
)

// Accuracy-vs-target row with a center-anchored deviation bar (center = on target)
const AccuracyRow = ({
  label,
  actual,
  target,
  variancePercent
}: {
  label: string
  actual: string
  target: string
  variancePercent: number
}) => {
  const fill = variancePercent > 0 ? 'bg-amber-500' : variancePercent < 0 ? 'bg-red-500' : 'bg-green-500'
  // Map deviation to a half-track width; ±20% spans the full half, with a sliver minimum so small misses still register
  const magnitude = Math.min(Math.abs(variancePercent), 20) / 20 * 50
  const width = variancePercent === 0 ? 0 : Math.max(magnitude, 3)
  const over = variancePercent > 0
  return (
    <Tooltip
      as="div"
      className="space-y-1.5"
      content={`${label}: the generated plan totals ${actual} against a ${target} goal target. ${VARIANCE_HINT}`}
    >
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="font-medium text-foreground">{label}</span>
        <span className="text-muted-foreground">
          <span className="font-semibold text-foreground">{actual}</span>
          <span className="mx-1">→</span>
          {target} target
          <span className={`ml-1.5 font-semibold ${varianceClass(variancePercent)}`}>
            {variancePercent > 0 ? '+' : ''}{variancePercent.toFixed(1)}%
          </span>
        </span>
      </div>
      <div className="relative h-2 rounded-full bg-muted">
        <div className="absolute left-1/2 top-1/2 h-3.5 w-px -translate-x-1/2 -translate-y-1/2 bg-border" />
        <div
          className={`absolute top-0 h-full ${over ? 'rounded-r-full' : 'rounded-l-full'} ${fill}`}
          style={over ? { left: '50%', width: `${width}%` } : { right: '50%', width: `${width}%` }}
        />
      </div>
    </Tooltip>
  )
}

const formatMultiplier = (value: number) => `${value.toFixed(1)}×`

export const TargetMultiplierDiagnostics = ({
  diagnostics,
  measurements
}: {
  diagnostics: PlanTargetMultiplierDiagnosticsDto
  measurements: MeasurementHelpers
}) => {
  const peakDistanceTargetKm = diagnostics.baseDistanceKm * diagnostics.appliedDistanceMultiplier
  const peakElevationTargetMeters = diagnostics.baseElevationMeters * diagnostics.appliedElevationMultiplier
  const altitude = measurements.format(diagnostics.maxAltitudeMeters, 'elevation')
  const altitudeThreshold = measurements.format(diagnostics.highAltitudeThresholdMeters, 'elevation')
  const configuredElevation = formatMultiplier(diagnostics.configuredElevationMultiplier)
  const appliedElevation = formatMultiplier(diagnostics.appliedElevationMultiplier)
  const altitudeExplanation = diagnostics.highAltitudeElevationFloorApplied
    ? `High-altitude override applied: ${altitude} meets the ${altitudeThreshold} threshold, raising elevation from ${configuredElevation} to ${appliedElevation}.`
    : diagnostics.highAltitudeElevationFloorEligible
      ? `High-altitude floor checked: ${altitude} meets the ${altitudeThreshold} threshold, but ${configuredElevation} already meets the ${formatMultiplier(diagnostics.highAltitudeMinimumMultiplier)} minimum, so no override was needed.`
      : `No high-altitude override: ${altitude} is below the ${altitudeThreshold} threshold.`

  return (
    <div className="rounded-lg border border-border/60 bg-muted/20 p-3" data-testid="target-multiplier-diagnostics">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-primary" />
          <p className="text-sm font-semibold text-foreground">Applied target multipliers</p>
        </div>
        <span className="rounded-full bg-primary/10 px-2 py-0.5 font-mono text-[10px] font-semibold text-primary">
          Fitness band L{diagnostics.difficultyLevel}
        </span>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <div className="rounded-md bg-background/70 px-3 py-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs font-medium text-muted-foreground">Distance</span>
            <span className="text-sm font-bold text-foreground">
              {formatMultiplier(diagnostics.appliedDistanceMultiplier)} applied
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {measurements.format(diagnostics.baseDistanceKm, 'distance')} base from {diagnostics.baseDistanceDayCount} day{diagnostics.baseDistanceDayCount === 1 ? '' : 's'} × {formatMultiplier(diagnostics.appliedDistanceMultiplier)} ={' '}
            <span className="font-semibold text-foreground">
              {measurements.format(peakDistanceTargetKm, 'distance')} peak target
            </span>
          </p>
        </div>
        <div className="rounded-md bg-background/70 px-3 py-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs font-medium text-muted-foreground">Elevation</span>
            <span className="text-sm font-bold text-foreground">
              {appliedElevation} applied
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {measurements.format(diagnostics.baseElevationMeters, 'elevation')} base from {diagnostics.baseElevationDayCount} day{diagnostics.baseElevationDayCount === 1 ? '' : 's'} × {appliedElevation} ={' '}
            <span className="font-semibold text-foreground">
              {measurements.format(peakElevationTargetMeters, 'elevation')} peak target
            </span>
          </p>
        </div>
      </div>
      <p className={`mt-2 text-xs ${diagnostics.highAltitudeElevationFloorApplied ? 'font-medium text-amber-700' : 'text-muted-foreground'}`}>
        {altitudeExplanation}
      </p>
      <p className="mt-1 text-[11px] text-muted-foreground">
        These multipliers calculate the goal target. Weekly floors, progression, and assignment rounding can make the final assigned totals differ.
      </p>
    </div>
  )
}

const StatBlock = ({
  label,
  value,
  hint
}: {
  label: string
  value: string | number
  hint?: string
}) => (
  <Tooltip as="div" content={hint} className="rounded-lg bg-muted/40 px-3 py-2">
    <p className="text-[11px] text-muted-foreground">{label}</p>
    <p className="text-base font-bold text-foreground">{value}</p>
  </Tooltip>
)

const GenerationWarningList = ({
  title = 'Warnings',
  warnings
}: {
  title?: string
  warnings: string[]
}) => (
  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
    <div className="mb-2 flex items-center gap-2 text-xs font-semibold">
      <AlertCircle className="h-3.5 w-3.5" />
      {title} ({warnings.length})
    </div>
    <ul className="space-y-1 text-xs">
      {warnings.map((warning, index) => (
        <li key={`${warning}-${index}`} className="flex gap-2">
          <span className="mt-[0.45rem] h-1 w-1 shrink-0 rounded-full bg-amber-600" />
          <span>{warning}</span>
        </li>
      ))}
    </ul>
  </div>
)

const GenerationTraceList = ({
  entries,
  measurements
}: {
  entries: PlanGenerationTraceEntryDto[]
  measurements: MeasurementHelpers
}) => {
  return (
    <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2">
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-primary">
        <FileText className="h-3.5 w-3.5" />
        Generation Trace ({entries.length})
      </div>
      <ul className="space-y-1.5">
        {entries.map((entry, index) => {
          const matchedRule = CODIFIED_PLAN_RULES_BY_ID[entry.rule]
          const ruleBadgeClass = 'rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[10px] text-primary'
          return (
            <li
              key={`${entry.rule}-${entry.action}-${entry.date ?? 'week'}-${index}`}
              className="rounded border border-border/60 bg-background px-2 py-1.5 text-xs"
            >
              <div className="flex flex-wrap items-center gap-1.5">
                {matchedRule ? (
                  <a href={`#${entry.rule}`} title={matchedRule.title} className={cn(ruleBadgeClass, 'hover:underline')}>
                    {entry.rule}
                  </a>
                ) : (
                  <span className={ruleBadgeClass}>
                    {entry.rule}
                  </span>
                )}
                <span className="font-medium">{entry.action}</span>
                {matchedRule && (
                  <span className="text-muted-foreground">{matchedRule.title}</span>
                )}
                {entry.date && (
                  <span className="text-muted-foreground">
                    <span className="font-medium text-foreground">{formatTraceWeekday(entry.date)}</span>
                    {' · '}{formatTraceDate(entry.date)}
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-muted-foreground">{entry.detail}</p>
              {entry.contexts && entry.contexts.length > 0 && (
                <div className="mt-2 space-y-1 border-l border-border/70 pl-2">
                  {entry.contexts.map((context, contextIndex) => {
                    const metrics = formatTraceContextMetrics(context, measurements)
                    return (
                      <div key={contextIndex} className="space-y-0.5 text-[11px]">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="font-medium text-foreground">
                            {context.summary || (context.date ? formatTraceDate(context.date) : 'Trace context')}
                          </span>
                          {context.reason && (
                            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">
                              {context.reason}
                            </span>
                          )}
                          {context.dayType && (
                            <span className="rounded bg-muted px-1.5 py-0.5 text-muted-foreground">
                              {context.dayType}
                            </span>
                          )}
                          {metrics && (
                            <span className="text-muted-foreground">{metrics}</span>
                          )}
                        </div>
                        {context.exercises.length > 0 && (
                          <p className="text-muted-foreground">
                            {context.exercises.join(' + ')}
                          </p>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

const resolveWarningWeekNumber = (warning: string) => {
  const match = warning.match(/\bweek\s+(\d+)\b/i)
  if (!match) return null

  const weekNumber = Number(match[1])
  return Number.isFinite(weekNumber) && weekNumber > 0 ? weekNumber : null
}

const groupWarningsByWeek = (warnings: string[]) => {
  const byWeek = new Map<number, string[]>()
  const global: string[] = []

  for (const warning of warnings) {
    const weekNumber = resolveWarningWeekNumber(warning)
    if (!weekNumber) {
      global.push(warning)
      continue
    }

    const list = byWeek.get(weekNumber) ?? []
    list.push(warning)
    byWeek.set(weekNumber, list)
  }

  return { byWeek, global }
}

const groupTraceEntriesByWeek = (entries: PlanGenerationTraceEntryDto[]) => {
  const byWeek = new Map<number, PlanGenerationTraceEntryDto[]>()

  for (const entry of entries) {
    const list = byWeek.get(entry.weekNumber) ?? []
    list.push(entry)
    byWeek.set(entry.weekNumber, list)
  }

  return byWeek
}

type PlanTesterMarkdownExportOptions = {
  exportedAt: Date
  profile: MockProfile
  goalInputs: GoalInputs
  planParams: PlanParams
  activeConfig: PlanGenerationConfigDto | null
  useConfigOverride: boolean
  configOverride: PlanGenerationConfigDto | null
  result: TestPlanGenerationResponse
  measurements: MeasurementHelpers
  popularHikes: PopularHike[]
  operators: TourOperator[]
  fitnessTargetOptions: Array<{ level: GeneralFitnessLevel; label: string }>
  warningGroups: ReturnType<typeof groupWarningsByWeek>
  traceByWeek: ReturnType<typeof groupTraceEntriesByWeek>
}

const labelForOption = <T extends string>(
  options: Array<{ value: T; label: string }>,
  value: T
) => options.find(option => option.value === value)?.label ?? value

const labelsForOptions = <T extends string>(
  options: Array<{ value: T; label: string }>,
  values: T[]
) => values.map(value => labelForOption(options, value))

const formatListValue = (values: string[]) => values.length > 0 ? values.join(', ') : 'None'

const formatNumberForExport = (value: number, maximumFractionDigits = 2) =>
  new Intl.NumberFormat(undefined, {
    maximumFractionDigits,
    minimumFractionDigits: 0
  }).format(value)

const formatDistanceForExport = (distanceKm: number, measurements: MeasurementHelpers) =>
  `${measurements.format(distanceKm, 'distance')} (raw ${formatNumberForExport(distanceKm, 3)} km)`

const formatElevationForExport = (elevationMeters: number, measurements: MeasurementHelpers) =>
  `${measurements.format(elevationMeters, 'elevation')} (raw ${formatNumberForExport(elevationMeters, 1)} m)`

const formatWeightForExport = (weightKg: number, measurements: MeasurementHelpers) =>
  `${measurements.format(weightKg, 'weight')} (raw ${formatNumberForExport(weightKg, 3)} kg)`

const formatSignedDistanceForExport = (distanceKm: number, measurements: MeasurementHelpers) => {
  const sign = distanceKm > 0 ? '+' : distanceKm < 0 ? '-' : ''
  return `${sign}${formatDistanceForExport(Math.abs(distanceKm), measurements)}`
}

const formatSignedElevationForExport = (elevationMeters: number, measurements: MeasurementHelpers) => {
  const sign = elevationMeters > 0 ? '+' : elevationMeters < 0 ? '-' : ''
  return `${sign}${formatElevationForExport(Math.abs(elevationMeters), measurements)}`
}

const formatSignedWeightForExport = (weightKg: number, measurements: MeasurementHelpers) => {
  const sign = weightKg > 0 ? '+' : weightKg < 0 ? '-' : ''
  return `${sign}${formatWeightForExport(Math.abs(weightKg), measurements)}`
}

const formatSignedPercent = (fraction: number) => {
  const percent = fraction * 100
  if (Math.abs(percent) < 0.05) return '0.0%'
  return `${percent > 0 ? '+' : ''}${percent.toFixed(1)}%`
}

const getReductionFraction = (rawValue: number, feasibleValue: number) => {
  if (rawValue <= 0) return 0
  return Math.max(0, (rawValue - feasibleValue) / rawValue)
}

const formatTargetComparisonVarianceForExport = (
  actualValue: number,
  targetValue: number,
  varianceFraction: number,
  kind: 'distance' | 'elevation' | 'weight',
  measurements: MeasurementHelpers
) => {
  const delta = actualValue - targetValue
  const formattedDelta = kind === 'distance'
    ? formatSignedDistanceForExport(delta, measurements)
    : kind === 'elevation'
      ? formatSignedElevationForExport(delta, measurements)
      : formatSignedWeightForExport(delta, measurements)

  return `${formatSignedPercent(varianceFraction)} (${formattedDelta})`
}

const formatOptionalDistanceDebtForExport = (
  distanceKm: number | null | undefined,
  measurements: MeasurementHelpers
) => distanceKm == null ? '—' : formatDistanceForExport(distanceKm, measurements)

const formatOptionalElevationDebtForExport = (
  elevationMeters: number | null | undefined,
  measurements: MeasurementHelpers
) => elevationMeters == null ? '—' : formatElevationForExport(elevationMeters, measurements)

const addOptionalNumbers = (values: Array<number | null | undefined>) =>
  values.reduce<number>((sum, value) => sum + (value ?? 0), 0)

const escapeMarkdownTableCell = (value: string) =>
  value.replace(/\|/g, '\\|').replace(/\n/g, '<br>')

const formatWaterfallNotes = (notes: string[], ruleIds: string[]) => {
  const parts = [
    ...ruleIds.map(ruleId => `rule: ${ruleId}`),
    ...notes
  ]
  return parts.length > 0 ? parts.join('; ') : 'None'
}

const targetWaterfallWeeklyStageIds = new Set([
  'week.initial-distribution',
  'durability.after-elevation-soft-cap',
  'durability.after-ttl-cap',
  'durability.after-debt-created',
  'durability.debt-repaid-before-concentration',
  'milestone.after-protected-windows',
  'rounding.after-assignment-rounding',
  'final.generated-workouts'
])

const appendTargetComparisonMarkdown = (
  lines: string[],
  comparison: PlanTargetComparisonDto | null | undefined,
  measurements: MeasurementHelpers
) => {
  lines.push('## Target Comparison', '')

  if (!comparison) {
    lines.push('None', '')
    return
  }

  if (comparison.warnings.length > 0) {
    lines.push('### Target Comparison Warnings', '')
    comparison.warnings.forEach(warning => lines.push(`- ${warning}`))
    lines.push('')
  }

  lines.push(
    '| Metric | Raw goal target | Feasible plan target | Final assigned workouts | Final vs raw | Final vs feasible |',
    '| --- | ---: | ---: | ---: | ---: | ---: |',
    [
      'Distance',
      formatDistanceForExport(comparison.rawGoalDistanceKm, measurements),
      formatDistanceForExport(comparison.feasiblePlanDistanceKm, measurements),
      formatDistanceForExport(comparison.finalWorkoutDistanceKm, measurements),
      formatTargetComparisonVarianceForExport(
        comparison.finalWorkoutDistanceKm,
        comparison.rawGoalDistanceKm,
        comparison.distanceVarianceFromRawFraction,
        'distance',
        measurements
      ),
      formatTargetComparisonVarianceForExport(
        comparison.finalWorkoutDistanceKm,
        comparison.feasiblePlanDistanceKm,
        comparison.distanceVarianceFromFeasibleFraction,
        'distance',
        measurements
      )
    ].join(' | ').replace(/^/, '| ').replace(/$/, ' |'),
    [
      'Elevation',
      formatElevationForExport(comparison.rawGoalElevationMeters, measurements),
      formatElevationForExport(comparison.feasiblePlanElevationMeters, measurements),
      formatElevationForExport(comparison.finalWorkoutElevationMeters, measurements),
      formatTargetComparisonVarianceForExport(
        comparison.finalWorkoutElevationMeters,
        comparison.rawGoalElevationMeters,
        comparison.elevationVarianceFromRawFraction,
        'elevation',
        measurements
      ),
      formatTargetComparisonVarianceForExport(
        comparison.finalWorkoutElevationMeters,
        comparison.feasiblePlanElevationMeters,
        comparison.elevationVarianceFromFeasibleFraction,
        'elevation',
        measurements
      )
    ].join(' | ').replace(/^/, '| ').replace(/$/, ' |'),
    [
      'Pack',
      formatWeightForExport(comparison.rawGoalPackWeightKg, measurements),
      formatWeightForExport(comparison.feasiblePlanPackWeightKg, measurements),
      formatWeightForExport(comparison.finalMaxWorkoutPackWeightKg, measurements),
      formatTargetComparisonVarianceForExport(
        comparison.finalMaxWorkoutPackWeightKg,
        comparison.rawGoalPackWeightKg,
        comparison.packVarianceFromRawFraction,
        'weight',
        measurements
      ),
      formatTargetComparisonVarianceForExport(
        comparison.finalMaxWorkoutPackWeightKg,
        comparison.feasiblePlanPackWeightKg,
        comparison.packVarianceFromFeasibleFraction,
        'weight',
        measurements
      )
    ].join(' | ').replace(/^/, '| ').replace(/$/, ' |'),
    ''
  )

  lines.push('### Debt Accounting', '')
  lines.push(
    '| Debt source | Distance created | Distance repaid | Distance unpaid | Elevation created | Elevation repaid | Elevation unpaid |',
    '| --- | ---: | ---: | ---: | ---: | ---: | ---: |'
  )

  const debtRows = [
    {
      source: 'Durability TTL',
      distanceCreatedKm: comparison.durabilityDistanceDebtCreatedKm,
      elevationCreatedMeters: comparison.durabilityElevationDebtCreatedMeters,
      distanceRepaidKm: comparison.durabilityDistanceDebtRepaidKm,
      elevationRepaidMeters: comparison.durabilityElevationDebtRepaidMeters,
      distanceUnpaidKm: comparison.durabilityDistanceDebtUnpaidKm,
      elevationUnpaidMeters: comparison.durabilityElevationDebtUnpaidMeters
    },
    {
      source: 'Milestone/protected windows',
      distanceCreatedKm: comparison.milestoneDistanceDebtCreatedKm,
      elevationCreatedMeters: comparison.milestoneElevationDebtCreatedMeters,
      distanceRepaidKm: comparison.milestoneDistanceDebtRepaidKm,
      elevationRepaidMeters: comparison.milestoneElevationDebtRepaidMeters,
      distanceUnpaidKm: comparison.milestoneDistanceDebtUnpaidKm,
      elevationUnpaidMeters: comparison.milestoneElevationDebtUnpaidMeters
    },
    {
      source: 'Elevation concentration',
      distanceCreatedKm: null,
      elevationCreatedMeters: comparison.concentrationElevationDebtCreatedMeters,
      distanceRepaidKm: null,
      elevationRepaidMeters: null,
      distanceUnpaidKm: null,
      elevationUnpaidMeters: comparison.concentrationElevationDebtUnpaidMeters
    },
    {
      source: 'Total',
      distanceCreatedKm: addOptionalNumbers([
        comparison.durabilityDistanceDebtCreatedKm,
        comparison.milestoneDistanceDebtCreatedKm
      ]),
      elevationCreatedMeters: addOptionalNumbers([
        comparison.durabilityElevationDebtCreatedMeters,
        comparison.milestoneElevationDebtCreatedMeters,
        comparison.concentrationElevationDebtCreatedMeters
      ]),
      distanceRepaidKm: addOptionalNumbers([
        comparison.durabilityDistanceDebtRepaidKm,
        comparison.milestoneDistanceDebtRepaidKm
      ]),
      elevationRepaidMeters: addOptionalNumbers([
        comparison.durabilityElevationDebtRepaidMeters,
        comparison.milestoneElevationDebtRepaidMeters
      ]),
      distanceUnpaidKm: addOptionalNumbers([
        comparison.durabilityDistanceDebtUnpaidKm,
        comparison.milestoneDistanceDebtUnpaidKm
      ]),
      elevationUnpaidMeters: addOptionalNumbers([
        comparison.durabilityElevationDebtUnpaidMeters,
        comparison.milestoneElevationDebtUnpaidMeters,
        comparison.concentrationElevationDebtUnpaidMeters
      ])
    }
  ]

  debtRows.forEach(row => {
    lines.push([
      row.source,
      formatOptionalDistanceDebtForExport(row.distanceCreatedKm, measurements),
      formatOptionalDistanceDebtForExport(row.distanceRepaidKm, measurements),
      formatOptionalDistanceDebtForExport(row.distanceUnpaidKm, measurements),
      formatOptionalElevationDebtForExport(row.elevationCreatedMeters, measurements),
      formatOptionalElevationDebtForExport(row.elevationRepaidMeters, measurements),
      formatOptionalElevationDebtForExport(row.elevationUnpaidMeters, measurements)
    ].join(' | ').replace(/^/, '| ').replace(/$/, ' |'))
  })

  lines.push('')
}

const appendTargetWaterfallMarkdown = (
  lines: string[],
  targetWaterfall: PlanTargetWaterfallDto | null | undefined,
  measurements: MeasurementHelpers
) => {
  lines.push('## Target Waterfall', '')

  if (!targetWaterfall) {
    lines.push('None', '')
    return
  }

  lines.push(
    `- Raw target: ${formatDistanceForExport(targetWaterfall.originalDistanceKm, measurements)} / ${formatElevationForExport(targetWaterfall.originalElevationMeters, measurements)} / ${formatWeightForExport(targetWaterfall.originalTargetPackWeightKg, measurements)}`,
    `- Final workouts: ${formatDistanceForExport(targetWaterfall.finalWorkoutDistanceKm, measurements)} / ${formatElevationForExport(targetWaterfall.finalWorkoutElevationMeters, measurements)} / max pack ${formatWeightForExport(targetWaterfall.finalMaxPackWeightKg, measurements)}`,
    `- Distance debt: ${formatDistanceForExport(targetWaterfall.distanceDebtKm, measurements)} (${(targetWaterfall.distanceVarianceFraction * 100).toFixed(1)}%)`,
    `- Elevation debt: ${formatElevationForExport(targetWaterfall.elevationDebtMeters, measurements)} (${(targetWaterfall.elevationVarianceFraction * 100).toFixed(1)}%)`,
    ''
  )

  lines.push(
    '| Stage | Distance | Δ Distance | Elevation | Δ Elevation | Distance Debt | Elevation Debt | Notes |',
    '| --- | ---: | ---: | ---: | ---: | ---: | ---: | --- |'
  )

  targetWaterfall.stages
    .slice()
    .sort((a, b) => a.sequence - b.sequence)
    .forEach(stage => {
      const stageDistanceKm = stage.totalWorkoutDistanceKm ?? stage.totalWeekTargetDistanceKm
      const stageElevationMeters = stage.totalWorkoutElevationMeters ?? stage.totalWeekTargetElevationMeters
      lines.push([
        escapeMarkdownTableCell(`${stage.sequence}. ${stage.stageName} (${stage.stageId})`),
        formatDistanceForExport(stageDistanceKm, measurements),
        formatSignedDistanceForExport(stage.distanceDeltaFromPreviousKm, measurements),
        formatElevationForExport(stageElevationMeters, measurements),
        formatSignedElevationForExport(stage.elevationDeltaFromPreviousMeters, measurements),
        formatDistanceForExport(stage.distanceDebtFromOriginalKm, measurements),
        formatElevationForExport(stage.elevationDebtFromOriginalMeters, measurements),
        escapeMarkdownTableCell(formatWaterfallNotes(stage.notes, stage.ruleIds))
      ].join(' | ').replace(/^/, '| ').replace(/$/, ' |'))
    })

  lines.push('', '### Target Waterfall by Week', '')
  lines.push(
    '| Week | Phase | Stage | Target Distance | Target Elevation | Workout Distance | Workout Elevation | Notes |',
    '| --- | --- | --- | ---: | ---: | ---: | ---: | --- |'
  )

  targetWaterfall.stages
    .filter(stage => targetWaterfallWeeklyStageIds.has(stage.stageId))
    .sort((a, b) => a.sequence - b.sequence)
    .forEach(stage => {
      stage.weeks
        .slice()
        .sort((a, b) => a.weekNumber - b.weekNumber)
        .forEach(week => {
          lines.push([
            week.weekNumber.toString(),
            escapeMarkdownTableCell(week.phase),
            escapeMarkdownTableCell(stage.stageId),
            formatDistanceForExport(week.targetDistanceKm, measurements),
            formatElevationForExport(week.targetElevationMeters, measurements),
            week.workoutDistanceKm == null ? '-' : formatDistanceForExport(week.workoutDistanceKm, measurements),
            week.workoutElevationMeters == null ? '-' : formatElevationForExport(week.workoutElevationMeters, measurements),
            escapeMarkdownTableCell(formatWaterfallNotes(week.notes, week.ruleIds))
          ].join(' | ').replace(/^/, '| ').replace(/$/, ' |'))
        })
    })

  lines.push('')
}

const formatKgForExport = (kg: number) => `${kg.toFixed(2)} kg`

const formatLbForExport = (kg: number) => `${(kg * 2.20462).toFixed(1)} lb`

const formatPackRawSourceForExport = (resolved: ResolvedPackWeightDto) => {
  const unit = resolved.assumedUnit ?? resolved.sourceUnit ?? 'kg'
  if (resolved.sourceValue != null) {
    if (unit === 'lb') {
      return `${resolved.sourceValue.toFixed(2)} lb / ${formatKgForExport(resolved.valueKg)}`
    }

    if (unit === 'kg') {
      return `${resolved.sourceValue.toFixed(2)} kg / ${formatLbForExport(resolved.valueKg)}`
    }

    return `${resolved.sourceValue.toFixed(2)} ${unit} / ${formatKgForExport(resolved.valueKg)}`
  }

  return `${formatKgForExport(resolved.valueKg)} / ${formatLbForExport(resolved.valueKg)}`
}

const appendPackNormalizationMarkdown = (
  lines: string[],
  packWeightResolution: ResolvedPackWeightDto | null | undefined,
  measurements: MeasurementHelpers
) => {
  lines.push('## Pack Normalization', '')

  if (!packWeightResolution) {
    lines.push('None', '')
    return
  }

  const finalTargetKg = packWeightResolution.finalTargetKg ?? packWeightResolution.valueKg
  const generatedMaxWorkoutPackKg = packWeightResolution.generatedMaxWorkoutPackKg
  lines.push(
    `- Source text: ${packWeightResolution.sourceText ?? 'None'}`,
    `- Source unit: ${packWeightResolution.sourceUnit ?? 'None'}`,
    `- Assumed unit: ${packWeightResolution.assumedUnit ?? 'None'}`,
    `- Raw resolved pack: ${formatPackRawSourceForExport(packWeightResolution)}`,
    `- Canonical target pack kg: ${formatKgForExport(finalTargetKg)}`,
    `- Canonical target pack display: ${formatWeightForExport(finalTargetKg, measurements)}`,
    `- Generated max workout pack: ${generatedMaxWorkoutPackKg == null ? 'None' : `${formatKgForExport(generatedMaxWorkoutPackKg)} / ${formatWeightForExport(generatedMaxWorkoutPackKg, measurements)}`}`,
    `- Resolution path: ${packWeightResolution.resolutionPath}`,
    `- Ambiguous numeric: ${packWeightResolution.isAmbiguousNumeric ? 'Yes' : 'No'}`,
    `- Defaulted: ${packWeightResolution.wasDefaulted ? 'Yes' : 'No'}`,
    `- Canonical target capped or reduced: ${packWeightResolution.wasCapped ? 'Yes' : 'No'}`,
    `- Rounded for display: ${packWeightResolution.wasRoundedForDisplay ? 'Yes' : 'No'}`,
    `- Warning: ${packWeightResolution.warning ?? 'None'}`,
    ''
  )
}

const formatOptionalDateForExport = (date: string | null | undefined) => {
  if (!date) return 'None'
  const parsed = parseCalendarDate(date)
  const display = Number.isNaN(parsed.getTime())
    ? date
    : parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  return `${display} (${date})`
}

const formatTraceRuleForExport = (ruleId: string) => {
  const rule = CODIFIED_PLAN_RULES_BY_ID[ruleId]
  return rule ? `${ruleId} - ${rule.title}` : ruleId
}

const formatExportedAt = (date: Date) =>
  `${date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'long' })} (${date.toISOString()})`

const appendJsonBlock = (lines: string[], title: string, value: unknown) => {
  lines.push(`## ${title}`, '', '```json', JSON.stringify(value, null, 2), '```', '')
}

const formatGoalInputDetails = ({
  goalInputs,
  popularHikes,
  operators,
  fitnessTargetOptions,
  measurements
}: Pick<
  PlanTesterMarkdownExportOptions,
  'goalInputs' | 'popularHikes' | 'operators' | 'fitnessTargetOptions' | 'measurements'
>) => {
  const lines = [
    `- Goal type: ${goalTypeLabels[goalInputs.goalType] ?? goalInputs.goalType}`,
    `- Resolved goal id candidate: ${
      goalInputs.goalType === 'PopularHike' ? goalInputs.popularHikeId :
        goalInputs.goalType === 'TourOperatorHike' ? goalInputs.tourId :
          goalInputs.goalType === 'GeneralFitness' ? goalInputs.generalFitnessLevel :
            goalInputs.goalType === 'CustomHike' ? 'custom' :
              'None'
    }`
  ]

  if (goalInputs.goalType === 'PopularHike') {
    const hike = popularHikes.find(candidate => candidate.id === goalInputs.popularHikeId)
    lines.push(`- Selected hike: ${hike?.tripName ?? 'Unknown'}`)
    lines.push(`- Hike id: ${goalInputs.popularHikeId || 'None'}`)
    if (hike) {
      lines.push(`- Location: ${formatListValue([hike.city, hike.stateOrProvince, hike.country].filter(Boolean) as string[])}`)
      lines.push(`- Distance: ${hike.totalDistanceKm != null ? formatDistanceForExport(hike.totalDistanceKm, measurements) : 'Unknown'}`)
      lines.push(`- Elevation gain: ${hike.totalElevationGainMeters != null ? formatElevationForExport(hike.totalElevationGainMeters, measurements) : 'Unknown'}`)
      lines.push(`- Trip days: ${hike.tripNumberOfDays ?? 'Unknown'}`)
      lines.push(`- Difficulty: ${formatHikeDifficulty(parseHikeDifficulty(hike.difficultyLevel)) ?? 'Unrated'}`)
      lines.push(`- Pack weight source text: ${hike.packWeight ?? 'None'}`)
    }
  }

  if (goalInputs.goalType === 'TourOperatorHike') {
    const operator = operators.find(candidate => candidate.id === goalInputs.tourOperatorId)
    const tour = operator?.tours.find(candidate => candidate.id === goalInputs.tourId)
    const distanceKm = tour?.totalDistanceKm ?? tour?.itinerary.reduce((sum, day) => sum + (day.distanceKm ?? 0), 0)
    const elevationMeters = tour?.totalElevationGainMeters ?? tour?.itinerary.reduce((sum, day) => sum + (day.elevationGainMeters ?? 0), 0)
    lines.push(`- Tour operator: ${operator?.name ?? 'Unknown'}`)
    lines.push(`- Operator id: ${goalInputs.tourOperatorId || 'None'}`)
    lines.push(`- Tour: ${tour?.name ?? 'Unknown'}`)
    lines.push(`- Tour id: ${goalInputs.tourId || 'None'}`)
    lines.push(`- Tour distance: ${distanceKm != null ? formatDistanceForExport(distanceKm, measurements) : 'Unknown'}`)
    lines.push(`- Tour elevation gain: ${elevationMeters != null ? formatElevationForExport(elevationMeters, measurements) : 'Unknown'}`)
    lines.push(`- Itinerary days: ${tour?.itinerary.length ?? 'Unknown'}`)
  }

  if (goalInputs.goalType === 'GeneralFitness') {
    const target = fitnessTargetOptions.find(candidate => candidate.level === goalInputs.generalFitnessLevel)
    lines.push(`- Fitness target: ${target?.label ?? goalInputs.generalFitnessLevel}`)
  }

  if (goalInputs.goalType === 'CustomHike') {
    lines.push(`- Custom distance: ${formatDistanceForExport(goalInputs.customDistanceKm, measurements)}`)
    lines.push(`- Custom elevation gain: ${formatElevationForExport(goalInputs.customElevationMeters, measurements)}`)
    lines.push(`- Custom days: ${goalInputs.customDays}`)
  }

  return lines
}

export const buildPlanTesterMarkdownExport = ({
  exportedAt,
  profile,
  goalInputs,
  planParams,
  activeConfig,
  useConfigOverride,
  configOverride,
  result,
  measurements,
  popularHikes,
  operators,
  fitnessTargetOptions,
  warningGroups,
  traceByWeek
}: PlanTesterMarkdownExportOptions) => {
  const plan = result.plan
  const summary = result.summary
  const configUsed = useConfigOverride && configOverride ? configOverride : activeConfig
  const trainingDaysPerWeek = profile.preferredDays.length
  const lines: string[] = [
    `# HikeStrong Plan Tester Export${plan?.goalName ? ` - ${plan.goalName}` : ''}`,
    '',
    `- Exported at: ${formatExportedAt(exportedAt)}`,
    `- Page: Plan Generation Tester`,
    `- Result success: ${result.success ? 'Yes' : 'No'}`,
    `- Measurement display unit: ${measurements.unit === 'Imperial' ? 'Imperial (mi, ft, lb)' : 'Metric (km, m, kg)'}`,
    ''
  ]

  lines.push(
    '## Mock User Profile',
    '',
    `- Endurance activity: ${labelForOption(enduranceOptions, profile.enduranceFrequency)} (${profile.enduranceFrequency})`,
    `- Hiking frequency: ${labelForOption(hikingOptions, profile.hikingFrequency)} (${profile.hikingFrequency})`,
    `- True elevation access: ${formatListValue(labelsForOptions(trueElevationOptions, profile.trueElevationAccess))}`,
    `- Equipment access: ${formatListValue(labelsForOptions(equipmentOptions, profile.equipmentAccess))}`,
    `- Concerns: ${formatListValue(labelsForOptions(concernOptions, profile.concerns))}`,
    `- Preferred training days: ${formatListValue(profile.preferredDays)}`,
    `- Training days per week: ${trainingDaysPerWeek} (derived from preferred training days)`,
    `- Age range: ${labelForOption(ageRangeOptions, profile.ageRange)} (${profile.ageRange})`,
    `- Measurement unit: ${profile.measurementUnit}`,
    ''
  )

  lines.push('## Goal Selection', '', ...formatGoalInputDetails({
    goalInputs,
    popularHikes,
    operators,
    fitnessTargetOptions,
    measurements
  }), '')

  lines.push(
    '## Plan Parameters',
    '',
    `- Hike date enabled: ${planParams.hikeDateEnabled ? 'Yes' : 'No'}`,
    `- Hike date: ${planParams.hikeDateEnabled ? formatOptionalDateForExport(planParams.hikeDate) : 'None'}`,
    `- Duration override enabled: ${planParams.durationWeeksEnabled ? 'Yes' : 'No'}`,
    `- Duration override weeks: ${planParams.durationWeeksEnabled ? planParams.durationWeeks : 'None'}`,
    `- Pack weight override enabled: ${planParams.packWeightEnabled ? 'Yes' : 'No'}`,
    `- Pack weight override: ${planParams.packWeightEnabled ? formatWeightForExport(planParams.packWeightKg, measurements) : 'None'}`,
    ''
  )

  lines.push(
    '## Config',
    '',
    `- Active config: ${activeConfig ? `${activeConfig.name} (v${activeConfig.version})` : 'None loaded'}`,
    `- Config override used: ${useConfigOverride ? 'Yes' : 'No'}`,
    `- Config used for request: ${configUsed ? `${configUsed.name} (v${configUsed.version})` : 'Unknown'}`,
    ''
  )

  if (!result.success) {
    lines.push(
      '## Generation Error',
      '',
      result.errorMessage ?? 'Generation failed without an error message.',
      ''
    )
  }

  if (plan && summary) {
    lines.push(
      '## Plan Summary',
      '',
      `- Plan id: ${plan.id}`,
      `- User id: ${plan.userId}`,
      `- Goal: ${plan.goalName}`,
      `- Goal type: ${goalTypeLabels[plan.goalType] ?? plan.goalType}`,
      `- Goal id: ${plan.goalId}`,
      `- Config version: ${plan.configVersion ?? 'None'}`,
      `- Created at: ${plan.createdAt}`,
      `- Plan start date: ${formatOptionalDateForExport(plan.planStartDate)}`,
      `- Plan end date: ${formatOptionalDateForExport(plan.planEndDate)}`,
      `- Target hike date: ${formatOptionalDateForExport(plan.targetHikeDate)}`,
      `- Duration weeks: ${plan.durationWeeks}`,
      `- Final assigned workout distance: ${formatDistanceForExport(summary.totalPlanDistanceKm, measurements)}`,
      `- Feasible plan distance target: ${formatDistanceForExport(summary.targetDistanceKm, measurements)}`,
      `- Distance variance: ${summary.distanceVariancePercent.toFixed(1)}%`,
      `- Final assigned workout elevation: ${formatElevationForExport(summary.totalPlanElevationGainMeters, measurements)}`,
      `- Feasible plan elevation target: ${formatElevationForExport(summary.targetElevationGainMeters, measurements)}`,
      `- Elevation variance: ${summary.elevationVariancePercent.toFixed(1)}%`,
      `- Feasible plan pack target: ${formatWeightForExport(plan.targets.targetPackWeightKg, measurements)}`,
      `- Final assigned max pack: ${formatWeightForExport(result.targetComparison?.finalMaxWorkoutPackWeightKg ?? result.targetWaterfall?.finalMaxPackWeightKg ?? plan.targets.targetPackWeightKg, measurements)}`,
      `- Total workouts: ${summary.totalWorkouts}`,
      `- With pack / without pack: ${summary.packWorkouts} / ${summary.noPackWorkouts}`,
      `- Average weekly distance: ${formatDistanceForExport(summary.averageWeeklyDistanceKm, measurements)}`,
      `- Average weekly elevation: ${formatElevationForExport(summary.averageWeeklyElevationMeters, measurements)}`,
      `- True elevation workouts: ${summary.trueElevationWorkouts}`,
      `- Artificial elevation workouts: ${summary.artificialElevationWorkouts}`,
      ''
    )
  }

  appendTargetComparisonMarkdown(lines, result.targetComparison, measurements)
  appendTargetWaterfallMarkdown(lines, result.targetWaterfall, measurements)
  appendPackNormalizationMarkdown(lines, result.packWeightResolution, measurements)

  lines.push('## Warnings', '')
  if (result.warnings.length === 0) {
    lines.push('None', '')
  } else {
    if (warningGroups.global.length > 0) {
      lines.push('### Global Warnings', '')
      warningGroups.global.forEach(warning => lines.push(`- ${warning}`))
      lines.push('')
    }

    if (warningGroups.byWeek.size > 0) {
      lines.push('### Week Warnings', '')
      Array.from(warningGroups.byWeek.entries())
        .sort(([a], [b]) => a - b)
        .forEach(([weekNumber, warnings]) => {
          lines.push(`#### Week ${weekNumber}`)
          warnings.forEach(warning => lines.push(`- ${warning}`))
          lines.push('')
        })
    }
  }

  lines.push('## Generation Trace Overview', '')
  if (!result.trace || result.trace.length === 0) {
    lines.push('None', '')
  } else {
    lines.push(`- Total trace entries: ${result.trace.length}`, '')
  }

  if (plan) {
    lines.push('## Week-by-Week Breakdown', '')
    plan.weeks.forEach(week => {
      const weekWarnings = warningGroups.byWeek.get(week.weekNumber) ?? []
      const weekTraceEntries = traceByWeek.get(week.weekNumber) ?? []

      lines.push(
        `### Week ${week.weekNumber}`,
        '',
        `- Date range: ${formatOptionalDateForExport(week.weekStartDate)} to ${formatOptionalDateForExport(week.weekEndDate)}`,
        `- Flags: ${formatListValue(week.flags)}`,
        `- Target distance: ${formatDistanceForExport(week.targetDistanceKm, measurements)}`,
        `- Target elevation gain: ${formatElevationForExport(week.targetElevationGainMeters, measurements)}`,
        `- Target average pack weight: ${formatWeightForExport(week.targetAveragePackWeightKg, measurements)}`,
        `- Intensity factor: ${week.intensityFactor}`,
        `- Workout count: ${week.workouts.length}`,
        ''
      )

      if (weekWarnings.length > 0) {
        lines.push('#### Week Warnings', '')
        weekWarnings.forEach(warning => lines.push(`- ${warning}`))
        lines.push('')
      }

      if (weekTraceEntries.length > 0) {
        lines.push('#### Generation Trace', '')
        weekTraceEntries.forEach(entry => {
          const datePart = entry.date ? ` (${formatOptionalDateForExport(entry.date)})` : ''
          lines.push(`- ${formatTraceRuleForExport(entry.rule)}: ${entry.action}${datePart}`)
          lines.push(`  - Detail: ${entry.detail}`)
          if (entry.contexts.length > 0) {
            entry.contexts.forEach(context => {
              const metrics = formatTraceContextMetrics(context, measurements)
              lines.push(`  - Context: ${context.summary || 'Trace context'}`)
              if (context.date) lines.push(`    - Date: ${formatOptionalDateForExport(context.date)}`)
              if (context.dayOfWeek) lines.push(`    - Day: ${context.dayOfWeek}`)
              if (context.dayType) lines.push(`    - Day type: ${context.dayType}`)
              if (context.sessionType) lines.push(`    - Session type: ${context.sessionType}`)
              if (context.reason) lines.push(`    - Reason: ${context.reason}`)
              if (metrics) lines.push(`    - Metrics: ${metrics}`)
              if (context.exercises.length > 0) lines.push(`    - Exercises: ${context.exercises.join(', ')}`)
            })
          }
        })
        lines.push('')
      }

      lines.push('#### Workouts', '')
      week.workouts.forEach((workout, index) => {
        lines.push(
          `##### Workout ${index + 1}: ${workout.dayOfWeek}, ${formatOptionalDateForExport(workout.date)}`,
          '',
          `- Long workout: ${workout.isLongWorkout ? 'Yes' : 'No'}`,
          `- Milestone: ${workout.isMilestone ? 'Yes' : 'No'}`,
          `- Distance: ${formatDistanceForExport(workout.distanceKm, measurements)}`,
          `- Elevation gain: ${formatElevationForExport(workout.elevationGainMeters, measurements)}`,
          `- Pack weight: ${formatWeightForExport(workout.packWeightKg, measurements)}`,
          `- Elevation type: ${workout.elevationType}`,
          `- Estimated duration: ${workout.estimatedDurationMinutes} min`,
          `- Notes: ${workout.notes ?? 'None'}`,
          ''
        )

        if (workout.exercises.length > 0) {
          lines.push('Exercises:')
          workout.exercises.forEach(exercise => {
            const details = formatWorkoutExerciseDetails(exercise, {
              formatDistance: (km) => measurements.format(km, 'distance'),
              formatElevation: (meters) => measurements.format(meters, 'elevation')
            })
            lines.push(
              `- ${exercise.order}. ${exercise.name} (${exercise.code}) - ${exercise.category}; ` +
              `details: ${details || 'None'}; pack: ${exercise.usePack ? 'Yes' : 'No'}; notes: ${exercise.notes ?? 'None'}`
            )
          })
          lines.push('')
        }
      })

      if (week.coachNotes.length > 0) {
        lines.push('#### Coach Notes', '')
        week.coachNotes.forEach(note => {
          lines.push(`- ${note.reason} (${formatOptionalDateForExport(note.effectiveDate ?? note.createdAt)}): ${note.message}`)
          if (note.rationale) {
            lines.push(`  - Rationale: ${JSON.stringify(note.rationale)}`)
          }
        })
        lines.push('')
      }

      if (week.notes) {
        lines.push('#### Week Notes', '', week.notes, '')
      }
    })
  }

  appendJsonBlock(lines, 'Raw Export Data', {
    exportedAt: exportedAt.toISOString(),
    profile,
    goalInputs,
    planParams,
    activeConfig,
    useConfigOverride,
    configOverride,
    result
  })

  return `${lines.join('\n').trim()}\n`
}

const createPlanTesterExportFilename = (goalName: string | null | undefined, exportedAt: Date) => {
  const stamp = exportedAt.toISOString().replace(/[:.]/g, '-')
  const slug = (goalName ?? 'plan')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'plan'

  return `hikestrong-plan-export-${slug}-${stamp}.md`
}

const downloadMarkdownFile = (filename: string, markdown: string) => {
  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 0)
}

const TargetComparisonCard = ({
  comparison,
  measurements
}: {
  comparison: PlanTargetComparisonDto
  measurements: MeasurementHelpers
}) => {
  const distanceReductionFraction = getReductionFraction(comparison.rawGoalDistanceKm, comparison.feasiblePlanDistanceKm)
  const elevationReductionFraction = getReductionFraction(comparison.rawGoalElevationMeters, comparison.feasiblePlanElevationMeters)
  const hasLargeRawReduction = distanceReductionFraction > 0.10 || elevationReductionFraction > 0.10

  const comparisonRows = [
    {
      metric: 'Distance',
      raw: formatDistanceForExport(comparison.rawGoalDistanceKm, measurements),
      feasible: formatDistanceForExport(comparison.feasiblePlanDistanceKm, measurements),
      finalAssigned: formatDistanceForExport(comparison.finalWorkoutDistanceKm, measurements),
      finalVsRaw: formatTargetComparisonVarianceForExport(
        comparison.finalWorkoutDistanceKm,
        comparison.rawGoalDistanceKm,
        comparison.distanceVarianceFromRawFraction,
        'distance',
        measurements
      ),
      finalVsFeasible: formatTargetComparisonVarianceForExport(
        comparison.finalWorkoutDistanceKm,
        comparison.feasiblePlanDistanceKm,
        comparison.distanceVarianceFromFeasibleFraction,
        'distance',
        measurements
      ),
      highlightFeasible: distanceReductionFraction > 0.10,
      highlightRawVariance: Math.abs(comparison.distanceVarianceFromRawFraction) > 0.10
    },
    {
      metric: 'Elevation',
      raw: formatElevationForExport(comparison.rawGoalElevationMeters, measurements),
      feasible: formatElevationForExport(comparison.feasiblePlanElevationMeters, measurements),
      finalAssigned: formatElevationForExport(comparison.finalWorkoutElevationMeters, measurements),
      finalVsRaw: formatTargetComparisonVarianceForExport(
        comparison.finalWorkoutElevationMeters,
        comparison.rawGoalElevationMeters,
        comparison.elevationVarianceFromRawFraction,
        'elevation',
        measurements
      ),
      finalVsFeasible: formatTargetComparisonVarianceForExport(
        comparison.finalWorkoutElevationMeters,
        comparison.feasiblePlanElevationMeters,
        comparison.elevationVarianceFromFeasibleFraction,
        'elevation',
        measurements
      ),
      highlightFeasible: elevationReductionFraction > 0.10,
      highlightRawVariance: Math.abs(comparison.elevationVarianceFromRawFraction) > 0.10
    },
    {
      metric: 'Pack',
      raw: formatWeightForExport(comparison.rawGoalPackWeightKg, measurements),
      feasible: formatWeightForExport(comparison.feasiblePlanPackWeightKg, measurements),
      finalAssigned: formatWeightForExport(comparison.finalMaxWorkoutPackWeightKg, measurements),
      finalVsRaw: formatTargetComparisonVarianceForExport(
        comparison.finalMaxWorkoutPackWeightKg,
        comparison.rawGoalPackWeightKg,
        comparison.packVarianceFromRawFraction,
        'weight',
        measurements
      ),
      finalVsFeasible: formatTargetComparisonVarianceForExport(
        comparison.finalMaxWorkoutPackWeightKg,
        comparison.feasiblePlanPackWeightKg,
        comparison.packVarianceFromFeasibleFraction,
        'weight',
        measurements
      ),
      highlightFeasible: getReductionFraction(comparison.rawGoalPackWeightKg, comparison.feasiblePlanPackWeightKg) > 0.10,
      highlightRawVariance: Math.abs(comparison.packVarianceFromRawFraction) > 0.10
    }
  ]

  const debtRows = [
    {
      source: 'Durability TTL',
      distanceCreatedKm: comparison.durabilityDistanceDebtCreatedKm,
      elevationCreatedMeters: comparison.durabilityElevationDebtCreatedMeters,
      distanceRepaidKm: comparison.durabilityDistanceDebtRepaidKm,
      elevationRepaidMeters: comparison.durabilityElevationDebtRepaidMeters,
      distanceUnpaidKm: comparison.durabilityDistanceDebtUnpaidKm,
      elevationUnpaidMeters: comparison.durabilityElevationDebtUnpaidMeters
    },
    {
      source: 'Milestone/protected windows',
      distanceCreatedKm: comparison.milestoneDistanceDebtCreatedKm,
      elevationCreatedMeters: comparison.milestoneElevationDebtCreatedMeters,
      distanceRepaidKm: comparison.milestoneDistanceDebtRepaidKm,
      elevationRepaidMeters: comparison.milestoneElevationDebtRepaidMeters,
      distanceUnpaidKm: comparison.milestoneDistanceDebtUnpaidKm,
      elevationUnpaidMeters: comparison.milestoneElevationDebtUnpaidMeters
    },
    {
      source: 'Elevation concentration',
      distanceCreatedKm: null,
      elevationCreatedMeters: comparison.concentrationElevationDebtCreatedMeters,
      distanceRepaidKm: null,
      elevationRepaidMeters: null,
      distanceUnpaidKm: null,
      elevationUnpaidMeters: comparison.concentrationElevationDebtUnpaidMeters
    },
    {
      source: 'Total',
      distanceCreatedKm: addOptionalNumbers([
        comparison.durabilityDistanceDebtCreatedKm,
        comparison.milestoneDistanceDebtCreatedKm
      ]),
      elevationCreatedMeters: addOptionalNumbers([
        comparison.durabilityElevationDebtCreatedMeters,
        comparison.milestoneElevationDebtCreatedMeters,
        comparison.concentrationElevationDebtCreatedMeters
      ]),
      distanceRepaidKm: addOptionalNumbers([
        comparison.durabilityDistanceDebtRepaidKm,
        comparison.milestoneDistanceDebtRepaidKm
      ]),
      elevationRepaidMeters: addOptionalNumbers([
        comparison.durabilityElevationDebtRepaidMeters,
        comparison.milestoneElevationDebtRepaidMeters
      ]),
      distanceUnpaidKm: addOptionalNumbers([
        comparison.durabilityDistanceDebtUnpaidKm,
        comparison.milestoneDistanceDebtUnpaidKm
      ]),
      elevationUnpaidMeters: addOptionalNumbers([
        comparison.durabilityElevationDebtUnpaidMeters,
        comparison.milestoneElevationDebtUnpaidMeters,
        comparison.concentrationElevationDebtUnpaidMeters
      ])
    }
  ]

  return (
    <Card className={hasLargeRawReduction ? 'border-amber-500' : undefined}>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-primary" />
          <CardTitle className="text-base">Target Comparison</CardTitle>
        </div>
        <CardDescription>Raw goal target, feasible plan target, and final assigned workouts</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {comparison.warnings.length > 0 && (
          <GenerationWarningList title="Target Comparison Warnings" warnings={comparison.warnings} />
        )}

        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 font-medium">Metric</th>
                <th className="py-2 pr-3 font-medium">Raw goal target</th>
                <th className="py-2 pr-3 font-medium">Feasible plan target</th>
                <th className="py-2 pr-3 font-medium">Final assigned workouts</th>
                <th className="py-2 pr-3 font-medium">Final vs raw</th>
                <th className="py-2 font-medium">Final vs feasible</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map(row => (
                <tr key={row.metric} className="border-b border-border/50 last:border-0">
                  <td className="py-2 pr-3 font-medium text-foreground">{row.metric}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{row.raw}</td>
                  <td className={cn(
                    'py-2 pr-3 text-muted-foreground',
                    row.highlightFeasible && 'font-semibold text-amber-700'
                  )}>{row.feasible}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{row.finalAssigned}</td>
                  <td className={cn(
                    'py-2 pr-3 text-muted-foreground',
                    row.highlightRawVariance && 'font-semibold text-amber-700'
                  )}>{row.finalVsRaw}</td>
                  <td className="py-2 text-muted-foreground">{row.finalVsFeasible}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="space-y-2">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Debt Accounting
          </p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-xs">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">Debt source</th>
                  <th className="py-2 pr-3 font-medium">Distance created</th>
                  <th className="py-2 pr-3 font-medium">Distance repaid</th>
                  <th className="py-2 pr-3 font-medium">Distance unpaid</th>
                  <th className="py-2 pr-3 font-medium">Elevation created</th>
                  <th className="py-2 pr-3 font-medium">Elevation repaid</th>
                  <th className="py-2 font-medium">Elevation unpaid</th>
                </tr>
              </thead>
              <tbody>
                {debtRows.map(row => (
                  <tr key={row.source} className="border-b border-border/50 last:border-0">
                    <td className="py-2 pr-3 font-medium text-foreground">{row.source}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{formatOptionalDistanceDebtForExport(row.distanceCreatedKm, measurements)}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{formatOptionalDistanceDebtForExport(row.distanceRepaidKm, measurements)}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{formatOptionalDistanceDebtForExport(row.distanceUnpaidKm, measurements)}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{formatOptionalElevationDebtForExport(row.elevationCreatedMeters, measurements)}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{formatOptionalElevationDebtForExport(row.elevationRepaidMeters, measurements)}</td>
                    <td className="py-2 text-muted-foreground">{formatOptionalElevationDebtForExport(row.elevationUnpaidMeters, measurements)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export const TrainingPlanTestPage = () => {
  // The admin's own account preference seeds the mock profile on first hydration.
  const account = useMeasurements()
  // State
  const [profile, setProfile] = useState<MockProfile>(() => ({
    ...defaultProfile,
    measurementUnit: account.unit
  }))
  const [goalInputs, setGoalInputs] = useState<GoalInputs>(defaultGoalInputs)
  const [planParams, setPlanParams] = useState<PlanParams>(defaultPlanParams)

  // All distance/elevation/pack values are generated and displayed in the mock
  // profile's unit, so what you see is what the generator rounded to.
  const measurements = useMemo(() => makeMeasurementHelpers(profile.measurementUnit), [profile.measurementUnit])

  // Keep the mock unit aligned with the admin's account preference until they
  // change it here — auth (and thus account.unit) may resolve after first render.
  const unitTouchedRef = useRef(false)
  useEffect(() => {
    if (unitTouchedRef.current) return
    setProfile(p => (p.measurementUnit === account.unit ? p : { ...p, measurementUnit: account.unit }))
  }, [account.unit])

  // Data loading
  const [popularHikes, setPopularHikes] = useState<PopularHike[]>([])
  const [operators, setOperators] = useState<TourOperator[]>([])
  const [fitnessHikeTargets, setFitnessHikeTargets] = useState<FitnessHikeTarget[]>([])
  const [activeConfig, setActiveConfig] = useState<PlanGenerationConfigDto | null>(null)
  const [loadingData, setLoadingData] = useState(true)

  // Generation state
  const [generating, setGenerating] = useState(false)
  const [result, setResult] = useState<TestPlanGenerationResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Config override state
  const [useConfigOverride, setUseConfigOverride] = useState(false)
  const [configOverride, setConfigOverride] = useState<PlanGenerationConfigDto | null>(null)

  // UI state
  const [expandedWeeks, setExpandedWeeks] = useState<Set<number>>(new Set())
  const [expandedWorkouts, setExpandedWorkouts] = useState<Set<string>>(new Set())
  const [showProfileSection, setShowProfileSection] = useState(true)
  const [showGoalSection, setShowGoalSection] = useState(true)
  const [showParamsSection, setShowParamsSection] = useState(true)
  const [showConfigSection, setShowConfigSection] = useState(false)

  const warningGroups = useMemo(() => groupWarningsByWeek(result?.warnings ?? []), [result?.warnings])
  const traceByWeek = useMemo(() => groupTraceEntriesByWeek(result?.trace ?? []), [result?.trace])

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setLoadingData(true)
      try {
        const [hikes, ops, fitnessTargets, config] = await Promise.all([
          getPopularHikes(),
          getTourOperators(),
          getFitnessHikeTargets(),
          getActiveConfig()
        ])
        setPopularHikes(hikes)
        setOperators(ops)
        setFitnessHikeTargets(fitnessTargets)
        setActiveConfig(config)
        // Initialize config override with active config values
        // (normalized so configs saved before newer tunables existed get defaults)
        if (config) {
          setConfigOverride(withPlanConfigDefaults(config))
        }

        // Set default selections
        if (hikes.length > 0) {
          setGoalInputs(prev => ({ ...prev, popularHikeId: hikes[0].id }))
        }
        if (ops.length > 0) {
          setGoalInputs(prev => ({ ...prev, tourOperatorId: ops[0].id }))
          if (ops[0].tours.length > 0) {
            setGoalInputs(prev => ({ ...prev, tourId: ops[0].tours[0].id }))
          }
        }
        const firstFitnessLevel = fitnessTargets
          .map(target => target.level.toString())
          .find(isGeneralFitnessLevel)
        if (firstFitnessLevel) {
          setGoalInputs(prev => isGeneralFitnessLevel(prev.generalFitnessLevel) &&
            fitnessTargets.some(target => target.level.toString() === prev.generalFitnessLevel)
            ? prev
            : { ...prev, generalFitnessLevel: firstFitnessLevel })
        }
      } catch (err) {
        console.error('Failed to load data', err)
        setError('Failed to load initial data')
      } finally {
        setLoadingData(false)
      }
    }
    void loadData()
  }, [])

  // Get tours for selected operator
  const selectedOperatorTours = useMemo(() => {
    const op = operators.find(o => o.id === goalInputs.tourOperatorId)
    return op?.tours ?? []
  }, [operators, goalInputs.tourOperatorId])

  const fitnessTargetOptions = useMemo(() => {
    const configuredTargets = fitnessHikeTargets
      .map(target => {
        const level = target.level.toString()
        if (!isGeneralFitnessLevel(level)) return null
        return {
          level,
          label: `Level ${level} - ${target.name}`
        }
      })
      .filter((target): target is { level: GeneralFitnessLevel; label: string } => target !== null)
      .sort((a, b) => Number(a.level) - Number(b.level))

    return configuredTargets.length > 0 ? configuredTargets : fallbackFitnessTargetOptions
  }, [fitnessHikeTargets])

  // Update tour selection when operator changes
  useEffect(() => {
    if (selectedOperatorTours.length > 0 && !selectedOperatorTours.find(t => t.id === goalInputs.tourId)) {
      setGoalInputs(prev => ({ ...prev, tourId: selectedOperatorTours[0].id }))
    }
  }, [selectedOperatorTours, goalInputs.tourId])

  // Build the goal ID based on goal type
  const resolvedGoalId = useMemo(() => {
    switch (goalInputs.goalType) {
      case 'PopularHike':
        return goalInputs.popularHikeId
      case 'TourOperatorHike':
        return goalInputs.tourId
      case 'GeneralFitness':
        return goalInputs.generalFitnessLevel
      case 'CustomHike':
        return 'custom'
      default:
        return ''
    }
  }, [goalInputs])

  // Build mock profile from UI state
  const buildMockProfile = useCallback((): MockUserProfileDto => {
    return {
      ageRange: profile.ageRange,
      trainingDaysPerWeek: profile.preferredDays.length,
      preferredDays: profile.preferredDays,
      enduranceFrequency: profile.enduranceFrequency,
      hikingFrequency: profile.hikingFrequency,
      trueElevationAccess: profile.trueElevationAccess,
      equipmentAccess: profile.equipmentAccess,
      concerns: profile.concerns,
      measurementUnit: profile.measurementUnit
    }
  }, [profile])

  // Generate handler
  const handleGenerate = useCallback(async () => {
    setGenerating(true)
    setError(null)
    setResult(null)

    try {
      const response = await testPlanGeneration({
        request: {
          userId: 'test-user', // Not used when mockProfile is provided
          goalType: goalInputs.goalType,
          goalId: resolvedGoalId,
          goalHikeStartDate: planParams.hikeDateEnabled ? planParams.hikeDate : null,
          customDurationWeeks: planParams.durationWeeksEnabled ? planParams.durationWeeks : null,
          overridePackWeightKg: planParams.packWeightEnabled ? planParams.packWeightKg : null
        },
        configOverride: useConfigOverride && configOverride ? configOverride : null,
        mockProfile: buildMockProfile()
      })

      setResult(response)

      // Auto-expand first week if successful
      if (response.success && response.plan?.weeks.length) {
        setExpandedWeeks(new Set([1]))
      }
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Generation failed'
        : 'Generation failed'
      setError(message)
    } finally {
      setGenerating(false)
    }
  }, [goalInputs.goalType, resolvedGoalId, planParams, useConfigOverride, configOverride, buildMockProfile])

  // Toggle helpers
  const toggleWeek = (weekNum: number) => {
    setExpandedWeeks(prev => {
      const next = new Set(prev)
      if (next.has(weekNum)) next.delete(weekNum)
      else next.add(weekNum)
      return next
    })
  }

  const toggleWorkout = (key: string) => {
    setExpandedWorkouts(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const expandAllWeeks = () => {
    if (result?.plan?.weeks) {
      setExpandedWeeks(new Set(result.plan.weeks.map(w => w.weekNumber)))
    }
  }

  // Jump-nav from the sticky toolbar: expand the target week, then scroll it under the toolbar
  const scrollToWeek = (weekNumber: number) => {
    setExpandedWeeks(prev => {
      const next = new Set(prev)
      next.add(weekNumber)
      return next
    })
    requestAnimationFrame(() => {
      document.getElementById(`plan-week-${weekNumber}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  const collapseAllWeeks = () => {
    setExpandedWeeks(new Set())
    setExpandedWorkouts(new Set())
  }

  const handleExportMarkdown = useCallback(() => {
    if (!result) return

    const exportedAt = new Date()
    const markdown = buildPlanTesterMarkdownExport({
      exportedAt,
      profile,
      goalInputs,
      planParams,
      activeConfig,
      useConfigOverride,
      configOverride,
      result,
      measurements,
      popularHikes,
      operators,
      fitnessTargetOptions,
      warningGroups,
      traceByWeek
    })

    downloadMarkdownFile(createPlanTesterExportFilename(result.plan?.goalName, exportedAt), markdown)
  }, [
    activeConfig,
    configOverride,
    fitnessTargetOptions,
    goalInputs,
    measurements,
    operators,
    planParams,
    popularHikes,
    profile,
    result,
    traceByWeek,
    useConfigOverride,
    warningGroups
  ])

  // Checkbox toggle helper
  const toggleArrayItem = <T,>(arr: T[], item: T): T[] => {
    return arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item]
  }

  const togglePreferredDay = (selectedDays: string[], day: string): string[] => {
    const next = toggleArrayItem(selectedDays, day)
    return next.length > 0 ? next : selectedDays
  }

  if (loadingData) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading data...</span>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <p className="text-sm uppercase tracking-wide text-muted-foreground">Training Plans</p>
        <h1 className="text-3xl font-semibold text-foreground">Plan Generation Tester</h1>
        <p className="text-sm text-muted-foreground">
          Test training plan generation with different user profiles and goals. Results are not saved.
        </p>
      </header>

      <div className="flex flex-wrap items-center justify-between gap-2">
        {activeConfig ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Settings className="h-3 w-3" />
            Active config: <span className="font-medium text-foreground">{activeConfig.name}</span> (v{activeConfig.version})
          </div>
        ) : <span />}
        <div className="flex items-center gap-2 rounded-full border border-border/60 bg-muted/30 px-3 py-1 text-xs">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Units</span>
          <span className="font-medium text-foreground">
            {measurements.unit === 'Imperial' ? 'Imperial · mi, ft, lb' : 'Metric · km, m, kg'}
          </span>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_28rem]">
        {/* Collapsible input panels, the Generate action, then results */}
        <div className="min-w-0 space-y-4">
          {/* Mock Profile */}
          <Card>
            <CardHeader className="cursor-pointer" onClick={() => setShowProfileSection(!showProfileSection)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-primary" />
                  <CardTitle className="text-base">Mock User Profile</CardTitle>
                </div>
                {showProfileSection ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </div>
              <CardDescription>Simulated onboarding data for plan generation</CardDescription>
            </CardHeader>
            {showProfileSection && (
              <CardContent className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="space-y-1">
                    <span className="text-xs font-medium text-muted-foreground">Endurance Activity</span>
                    <select
                      className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                      value={profile.enduranceFrequency}
                      onChange={e => setProfile(p => ({ ...p, enduranceFrequency: e.target.value as EnduranceFrequency }))}
                    >
                      {enduranceOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </label>
                  <label className="space-y-1">
                    <span className="text-xs font-medium text-muted-foreground">Hiking Frequency</span>
                    <select
                      className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                      value={profile.hikingFrequency}
                      onChange={e => setProfile(p => ({ ...p, hikingFrequency: e.target.value as HikingFrequency }))}
                    >
                      {hikingOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </label>
                </div>

                <div className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">True Elevation Access</span>
                  <div className="flex flex-wrap gap-2">
                    {trueElevationOptions.map(o => (
                      <label key={o.value} className="flex items-center gap-1.5 text-xs">
                        <input
                          type="checkbox"
                          checked={profile.trueElevationAccess.includes(o.value)}
                          onChange={() => setProfile(p => ({ ...p, trueElevationAccess: toggleArrayItem(p.trueElevationAccess, o.value) }))}
                          className="rounded border-border"
                        />
                        {o.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">Equipment Access</span>
                  <div className="flex flex-wrap gap-2">
                    {equipmentOptions.map(o => (
                      <label key={o.value} className="flex items-center gap-1.5 text-xs">
                        <input
                          type="checkbox"
                          checked={profile.equipmentAccess.includes(o.value)}
                          onChange={() => setProfile(p => ({ ...p, equipmentAccess: toggleArrayItem(p.equipmentAccess, o.value) }))}
                          className="rounded border-border"
                        />
                        {o.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">Concerns</span>
                  <div className="flex flex-wrap gap-2">
                    {concernOptions.map(o => (
                      <label key={o.value} className="flex items-center gap-1.5 text-xs">
                        <input
                          type="checkbox"
                          checked={profile.concerns.includes(o.value)}
                          onChange={() => setProfile(p => ({ ...p, concerns: toggleArrayItem(p.concerns, o.value) }))}
                          className="rounded border-border"
                        />
                        {o.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">Preferred Training Days</span>
                  <div className="flex flex-wrap gap-1">
                    {dayOptions.map(d => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setProfile(p => ({ ...p, preferredDays: togglePreferredDay(p.preferredDays, d) }))}
                        className={`rounded px-2 py-0.5 text-xs transition ${
                          profile.preferredDays.includes(d)
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                      >
                        {d.slice(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>

                <label className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">Age range</span>
                  <select
                    value={profile.ageRange}
                    onChange={e => setProfile(p => ({ ...p, ageRange: e.target.value as AgeRange }))}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  >
                    <option value="Range18To29">18–29</option>
                    <option value="Range30To39">30–39</option>
                    <option value="Range40To49">40–49</option>
                    <option value="Range50Plus">50+</option>
                  </select>
                </label>

                <div className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">Measurement units</span>
                  <select
                    value={profile.measurementUnit}
                    onChange={e => {
                      unitTouchedRef.current = true
                      setProfile(p => ({ ...p, measurementUnit: e.target.value as MeasurementUnit }))
                    }}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  >
                    <option value="Metric">Metric — km, m, kg</option>
                    <option value="Imperial">Imperial — mi, ft, lb</option>
                  </select>
                  <p className="text-[11px] text-muted-foreground">
                    The generator rounds assigned distance, elevation, and pack weight to clean
                    numbers in this unit. Defaults to your account preference ({account.unit}).
                  </p>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Goal Selection */}
          <Card>
            <CardHeader className="cursor-pointer" onClick={() => setShowGoalSection(!showGoalSection)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-primary" />
                  <CardTitle className="text-base">Goal Selection</CardTitle>
                </div>
                {showGoalSection ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </div>
              <CardDescription>Choose the type of hike to train for</CardDescription>
            </CardHeader>
            {showGoalSection && (
              <CardContent className="space-y-4">
                <label className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">Goal Type</span>
                  <select
                    className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                    value={goalInputs.goalType}
                    onChange={e => setGoalInputs(g => ({ ...g, goalType: e.target.value as GoalType }))}
                  >
                    <option value="PopularHike">Popular Hike</option>
                    <option value="TourOperatorHike">Tour Operator Hike</option>
                    <option value="GeneralFitness">Hiking Fitness</option>
                    <option value="CustomHike">Custom Hike</option>
                  </select>
                </label>

                {goalInputs.goalType === 'PopularHike' && (
                  <label className="space-y-1">
                    <span className="text-xs font-medium text-muted-foreground">Select Hike</span>
                    <select
                      className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                      value={goalInputs.popularHikeId}
                      onChange={e => setGoalInputs(g => ({ ...g, popularHikeId: e.target.value }))}
                    >
                      {popularHikes.map(h => (
                        <option key={h.id} value={h.id}>
                          {h.tripName} ({h.totalDistanceKm != null ? measurements.format(h.totalDistanceKm, 'distance') : '?'}, {h.totalElevationGainMeters != null ? measurements.format(h.totalElevationGainMeters, 'elevation') : '?'})
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                {goalInputs.goalType === 'TourOperatorHike' && (
                  <>
                    <label className="space-y-1">
                      <span className="text-xs font-medium text-muted-foreground">Tour Operator</span>
                      <select
                        className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                        value={goalInputs.tourOperatorId}
                        onChange={e => setGoalInputs(g => ({ ...g, tourOperatorId: e.target.value }))}
                      >
                        {operators.map(op => (
                          <option key={op.id} value={op.id}>{op.name} ({op.tours.length} tours)</option>
                        ))}
                      </select>
                    </label>
                    <label className="space-y-1">
                      <span className="text-xs font-medium text-muted-foreground">Tour</span>
                      <select
                        className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                        value={goalInputs.tourId}
                        onChange={e => setGoalInputs(g => ({ ...g, tourId: e.target.value }))}
                      >
                        {selectedOperatorTours.map(t => {
                          const dist = t.itinerary.reduce((sum, d) => sum + (d.distanceKm ?? 0), 0)
                          const elev = t.itinerary.reduce((sum, d) => sum + (d.elevationGainMeters ?? 0), 0)
                          return (
                            <option key={t.id} value={t.id}>
                              {t.name} ({measurements.format(dist, 'distance')}, {measurements.format(elev, 'elevation')})
                            </option>
                          )
                        })}
                      </select>
                    </label>
                  </>
                )}

                {goalInputs.goalType === 'GeneralFitness' && (
                  <label className="space-y-1">
                    <span className="text-xs font-medium text-muted-foreground">Fitness Level</span>
                    <select
                      className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                      value={goalInputs.generalFitnessLevel}
                      onChange={e => {
                        const selectedLevel = e.target.value
                        if (isGeneralFitnessLevel(selectedLevel)) {
                          setGoalInputs(g => ({ ...g, generalFitnessLevel: selectedLevel }))
                        }
                      }}
                    >
                      {fitnessTargetOptions.map(option => (
                        <option key={option.level} value={option.level}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                )}

                {goalInputs.goalType === 'CustomHike' && (
                  <div className="space-y-3">
                    <MeasurementField
                      label="Total Distance"
                      labelClassName="text-xs font-medium text-muted-foreground"
                      kind="distance"
                      unit={profile.measurementUnit}
                      min={1}
                      valueMetric={goalInputs.customDistanceKm}
                      emptyValue={80}
                      onValueMetricChange={(value) => setGoalInputs(g => ({ ...g, customDistanceKm: value ?? 80 }))}
                    />
                    <MeasurementField
                      label="Total Elevation Gain"
                      labelClassName="text-xs font-medium text-muted-foreground"
                      kind="elevation"
                      unit={profile.measurementUnit}
                      min={0}
                      valueMetric={goalInputs.customElevationMeters}
                      emptyValue={3000}
                      onValueMetricChange={(value) => setGoalInputs(g => ({ ...g, customElevationMeters: value ?? 3000 }))}
                    />
                    <label className="space-y-1">
                      <span className="text-xs font-medium text-muted-foreground">Number of Days</span>
                      <Input
                        type="number"
                        min={1}
                        max={30}
                        value={goalInputs.customDays}
                        onChange={e => setGoalInputs(g => ({ ...g, customDays: parseInt(e.target.value) || 5 }))}
                      />
                    </label>
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* Plan Parameters */}
          <Card>
            <CardHeader className="cursor-pointer" onClick={() => setShowParamsSection(!showParamsSection)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-primary" />
                  <CardTitle className="text-base">Plan Parameters</CardTitle>
                </div>
                {showParamsSection ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </div>
              <CardDescription>Timeline and overrides</CardDescription>
            </CardHeader>
            {showParamsSection && (
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={planParams.hikeDateEnabled}
                      onChange={e => setPlanParams(p => ({ ...p, hikeDateEnabled: e.target.checked }))}
                      className="rounded border-border"
                    />
                    <span className="text-sm">Set hike date</span>
                  </label>
                  {planParams.hikeDateEnabled && (
                    <Input
                      type="date"
                      value={planParams.hikeDate}
                      onChange={e => setPlanParams(p => ({ ...p, hikeDate: e.target.value }))}
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={planParams.durationWeeksEnabled}
                      onChange={e => setPlanParams(p => ({ ...p, durationWeeksEnabled: e.target.checked }))}
                      className="rounded border-border"
                    />
                    <span className="text-sm">Override duration (weeks)</span>
                  </label>
                  {planParams.durationWeeksEnabled && (
                    <Input
                      type="number"
                      min={4}
                      max={24}
                      value={planParams.durationWeeks}
                      onChange={e => setPlanParams(p => ({ ...p, durationWeeks: parseInt(e.target.value) || 12 }))}
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={planParams.packWeightEnabled}
                      onChange={e => setPlanParams(p => ({ ...p, packWeightEnabled: e.target.checked }))}
                      className="rounded border-border"
                    />
                    <span className="text-sm">Override pack weight ({measurements.unitLabel('weight')})</span>
                  </label>
                  {planParams.packWeightEnabled && (
                    <MeasurementField
                      label="Pack weight"
                      labelClassName="text-xs font-medium text-muted-foreground"
                      kind="weight"
                      unit={profile.measurementUnit}
                      min={0}
                      valueMetric={planParams.packWeightKg}
                      emptyValue={9}
                      onValueMetricChange={(value) => setPlanParams(p => ({ ...p, packWeightKg: value ?? 9 }))}
                    />
                  )}
                </div>
              </CardContent>
            )}
          </Card>

          {/* Config Override */}
          <Card className={useConfigOverride ? 'border-primary/50' : ''}>
            <CardHeader className="cursor-pointer" onClick={() => setShowConfigSection(!showConfigSection)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Settings className="h-4 w-4 text-primary" />
                  <CardTitle className="text-base">Config Override</CardTitle>
                  {useConfigOverride && (
                    <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                      Active
                    </span>
                  )}
                </div>
                {showConfigSection ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </div>
              <CardDescription>Test with modified config values (not saved)</CardDescription>
            </CardHeader>
            {showConfigSection && configOverride && (
              <CardContent className="space-y-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={useConfigOverride}
                    onChange={e => setUseConfigOverride(e.target.checked)}
                    className="rounded border-border"
                  />
                  <span className="text-sm font-medium">Use config override for testing</span>
                </label>

                {useConfigOverride && (
                  <>
                    <PlanConfigFormFields
                      value={configOverride}
                      onChange={setConfigOverride}
                      showExplainers={false}
                    />

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => activeConfig && setConfigOverride(withPlanConfigDefaults(activeConfig))}
                      className="w-full"
                    >
                      <RefreshCcw className="mr-2 h-3 w-3" />
                      Reset to Active Config
                    </Button>
                  </>
                )}
              </CardContent>
            )}
          </Card>

          {/* Generate */}
          <div className="border-t border-border/60 pt-3">
            <Button
              onClick={handleGenerate}
              disabled={generating || !resolvedGoalId}
              className="w-full"
              size="lg"
            >
              {generating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Generate Test Plan
                  {useConfigOverride && ' (with override)'}
                </>
              )}
            </Button>
          </div>

          {/* Results report */}
          {/* Sticky results toolbar: glance metrics, bulk controls, and week jump-nav for long plans */}
          {result?.success && result.plan && result.summary && (
            <div className="sticky top-0 z-20 rounded-xl border border-border/60 bg-card/95 px-4 py-3 shadow-sm backdrop-blur-md">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                <div className="mr-auto flex min-w-0 items-center gap-2">
                  <Mountain className="h-4 w-4 shrink-0 text-primary" />
                  <span className="truncate text-sm font-semibold text-foreground">{result.plan.goalName}</span>
                </div>
                <ToolbarStat
                  label="Weeks"
                  value={`${result.plan.durationWeeks}`}
                  hint="Number of training weeks in the generated plan."
                />
                <ToolbarStat
                  label="Assigned distance"
                  value={measurements.format(result.summary.totalPlanDistanceKm, 'distance')}
                  variance={result.summary.distanceVariancePercent}
                  target={measurements.format(result.summary.targetDistanceKm, 'distance')}
                  hint={`Total distance across every workout in the plan. ${VARIANCE_HINT}`}
                />
                <ToolbarStat
                  label="Assigned elevation"
                  value={measurements.format(result.summary.totalPlanElevationGainMeters, 'elevation')}
                  variance={result.summary.elevationVariancePercent}
                  target={measurements.format(result.summary.targetElevationGainMeters, 'elevation')}
                  hint={`Total elevation gain across every workout in the plan. ${VARIANCE_HINT}`}
                />
                <ToolbarStat
                  label="Workouts"
                  value={`${result.summary.totalWorkouts}`}
                  hint="Total number of workout sessions in the plan (excludes rest days)."
                />
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={expandAllWeeks}>Expand all</Button>
                  <Button variant="ghost" size="sm" onClick={collapseAllWeeks}>Collapse all</Button>
                </div>
              </div>
              {/* Week jump-nav */}
              <div className="mt-2.5 flex items-center gap-1.5 overflow-x-auto pb-0.5">
                <span className="shrink-0 font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Jump
                </span>
                {result.plan.weeks.map(week => {
                  const isPeak = week.flags.includes('PeakWeek')
                  const isTaper = week.flags.includes('TaperWeek')
                  return (
                    <Tooltip
                      key={week.weekNumber}
                      className="shrink-0"
                      content={week.flags.length > 0 ? week.flags.join(', ') : `Week ${week.weekNumber}`}
                    >
                      <button
                        type="button"
                        onClick={() => scrollToWeek(week.weekNumber)}
                        className={cn(
                          'cursor-pointer rounded-md border px-2 py-0.5 text-xs font-medium transition-colors',
                          isPeak
                            ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
                            : isTaper
                              ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                              : 'border-border/60 bg-muted/40 text-muted-foreground hover:border-primary/40 hover:text-primary'
                        )}
                      >
                        {week.weekNumber}
                      </button>
                    </Tooltip>
                  )
                })}
              </div>
            </div>
          )}

          {error && (
            <Card className="border-destructive">
              <CardContent className="flex items-center gap-2 py-4 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <span>{error}</span>
              </CardContent>
            </Card>
          )}

          {result && !result.success && (
            <Card className="border-destructive">
              <CardContent className="flex items-center gap-2 py-4 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <span>{result.errorMessage ?? 'Generation failed'}</span>
              </CardContent>
            </Card>
          )}

          {warningGroups.global.length > 0 && (
            <Card className="border-amber-500">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base text-amber-600">
                  <AlertCircle className="h-4 w-4" />
                  Global Warnings ({warningGroups.global.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="list-inside list-disc space-y-1 text-sm text-amber-700">
                  {warningGroups.global.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </CardContent>
            </Card>
          )}

          {result?.success && result.plan && result.summary && (
            <>
              {/* Summary Stats */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-primary" />
                      <CardTitle className="text-base">Plan Summary</CardTitle>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      Config: {result.plan.configVersion}
                    </span>
                  </div>
                  <CardDescription>{result.plan.goalName}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  {/* Accuracy vs target — the core question for a generation tester */}
                  <div className="space-y-3">
                    <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Accuracy vs target
                    </p>
                    <AccuracyRow
                      label="Distance"
                      actual={measurements.format(result.summary.totalPlanDistanceKm, 'distance')}
                      target={measurements.format(result.summary.targetDistanceKm, 'distance')}
                      variancePercent={result.summary.distanceVariancePercent}
                    />
                    <AccuracyRow
                      label="Elevation"
                      actual={measurements.format(result.summary.totalPlanElevationGainMeters, 'elevation')}
                      target={measurements.format(result.summary.targetElevationGainMeters, 'elevation')}
                      variancePercent={result.summary.elevationVariancePercent}
                    />
                  </div>

                  {result.targetMultiplierDiagnostics && (
                    <TargetMultiplierDiagnostics
                      diagnostics={result.targetMultiplierDiagnostics}
                      measurements={measurements}
                    />
                  )}

                  {/* Composition — diagnostics the toolbar can't carry */}
                  <div className="space-y-2">
                    <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Composition
                    </p>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      <StatBlock
                        label="Workouts"
                        value={result.summary.totalWorkouts}
                        hint="Total number of workout sessions in the plan (excludes rest days)."
                      />
                      <StatBlock
                        label="With / without pack"
                        value={`${result.summary.packWorkouts} / ${result.summary.noPackWorkouts}`}
                        hint="Count of workouts carrying a weighted pack vs without a pack."
                      />
                      <StatBlock
                        label="Avg weekly distance"
                        value={measurements.format(result.summary.averageWeeklyDistanceKm, 'distance')}
                        hint="Mean distance per week across the plan (total distance ÷ number of weeks)."
                      />
                      <StatBlock
                        label="True elevation"
                        value={result.summary.trueElevationWorkouts}
                        hint="Count of workouts that build elevation on real terrain (hills, trails, stairs, step-ups)."
                      />
                      <StatBlock
                        label="Artificial elevation"
                        value={result.summary.artificialElevationWorkouts}
                        hint="Count of workouts that simulate elevation (treadmill incline or stair machine)."
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {result.targetComparison && (
                <TargetComparisonCard
                  comparison={result.targetComparison}
                  measurements={measurements}
                />
              )}

              {/* Week-by-Week Breakdown */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Mountain className="h-4 w-4 text-primary" />
                    <CardTitle className="text-base">Week-by-Week Breakdown</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {result.plan.weeks.map(week => {
                    const weekWarnings = warningGroups.byWeek.get(week.weekNumber) ?? []
                    const weekTraceEntries = traceByWeek.get(week.weekNumber) ?? []

                    return (
                      <div
                        key={week.weekNumber}
                        id={`plan-week-${week.weekNumber}`}
                        className="scroll-mt-[7.5rem] rounded-lg border border-border"
                      >
                      {/* Week Header */}
                      <button
                        type="button"
                        onClick={() => toggleWeek(week.weekNumber)}
                        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-muted/50"
                      >
                        <div className="flex items-center gap-3">
                          {expandedWeeks.has(week.weekNumber) ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span className="font-semibold">Week {week.weekNumber}</span>
                          <div className="flex gap-1">
                            {week.flags.map(f => (
                              <span key={f} className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                                f === 'PeakWeek' ? 'bg-red-100 text-red-700' :
                                f === 'TaperWeek' ? 'bg-blue-100 text-blue-700' :
                                f === 'CompressedTimeline' ? 'bg-amber-100 text-amber-700' :
                                f === 'KneeAccommodation' ? 'bg-purple-100 text-purple-700' :
                                'bg-muted text-muted-foreground'
                              }`}>
                                {f}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <Tooltip content="Target total distance for this week">
                            {measurements.format(week.targetDistanceKm, 'distance')}
                          </Tooltip>
                          <Tooltip content="Target total elevation gain for this week">
                            {measurements.format(week.targetElevationGainMeters, 'elevation')}
                          </Tooltip>
                          <span>{week.workouts.length} workouts</span>
                          {weekWarnings.length > 0 && (
                            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
                              {weekWarnings.length} warning{weekWarnings.length === 1 ? '' : 's'}
                            </span>
                          )}
                          {weekTraceEntries.length > 0 && (
                            <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                              {weekTraceEntries.length} trace
                            </span>
                          )}
                        </div>
                      </button>

                      {/* Week Workouts */}
                      {expandedWeeks.has(week.weekNumber) && (
                        <div className="border-t border-border bg-muted/20 px-4 py-3">
                          {(weekWarnings.length > 0 || weekTraceEntries.length > 0) && (
                            <div className="mb-3 space-y-2">
                              {weekWarnings.length > 0 && (
                                <GenerationWarningList warnings={weekWarnings} />
                              )}
                              {weekTraceEntries.length > 0 && (
                                <GenerationTraceList entries={weekTraceEntries} measurements={measurements} />
                              )}
                            </div>
                          )}
                          <div className="space-y-2">
                            {week.workouts.map((workout, idx) => {
                              const workoutKey = `${week.weekNumber}-${idx}`
                              const workoutDateLabel = formatTraceDate(workout.date)
                              return (
                                <div key={workoutKey} className="rounded-md border border-border/60 bg-background">
                                  {/* Workout Header */}
                                  <button
                                    type="button"
                                    onClick={() => toggleWorkout(workoutKey)}
                                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted/30"
                                  >
                                    <div className="flex items-center gap-2">
                                      {expandedWorkouts.has(workoutKey) ? (
                                        <ChevronDown className="h-3 w-3 text-muted-foreground" />
                                      ) : (
                                        <ChevronRight className="h-3 w-3 text-muted-foreground" />
                                      )}
                                      <span className="font-medium">{workout.dayOfWeek}</span>
                                      <span className="text-muted-foreground">
                                        {workoutDateLabel}
                                      </span>
                                      {workout.isLongWorkout && (
                                        <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                                          Long
                                        </span>
                                      )}
                                      {workout.isMilestone && (
                                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                                          Milestone
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                      <Tooltip content="Workout distance">{measurements.format(workout.distanceKm, 'distance')}</Tooltip>
                                      <Tooltip content="Workout elevation gain">{measurements.format(workout.elevationGainMeters, 'elevation')}</Tooltip>
                                      {workout.packWeightKg > 0 && (
                                        <Tooltip className="text-primary" content="Pack weight carried for this workout">{measurements.format(workout.packWeightKg, 'weight')}</Tooltip>
                                      )}
                                      <Tooltip content="Estimated time to complete this workout">{workout.estimatedDurationMinutes} min</Tooltip>
                                      <Tooltip
                                        content={
                                          workout.elevationType === 'TrueElevation'
                                            ? 'Elevation type: true — built on real terrain (hills, trails, stairs, step-ups)'
                                            : workout.elevationType === 'ArtificialElevation'
                                              ? 'Elevation type: artificial — simulated (treadmill incline or stair machine)'
                                              : 'Elevation type: none — this workout has no elevation gain'
                                        }
                                        className={`rounded px-1 ${
                                        workout.elevationType === 'TrueElevation' ? 'bg-green-100 text-green-700' :
                                        workout.elevationType === 'ArtificialElevation' ? 'bg-blue-100 text-blue-700' :
                                        'bg-gray-100 text-gray-600'
                                      }`}>
                                        {workout.elevationType === 'TrueElevation' ? 'True' :
                                         workout.elevationType === 'ArtificialElevation' ? 'Artificial' : 'None'}
                                      </Tooltip>
                                    </div>
                                  </button>

                                  {/* Workout Exercises */}
                                  {expandedWorkouts.has(workoutKey) && workout.exercises.length > 0 && (
                                    <div className="border-t border-border/40 bg-muted/10 px-3 py-2">
                                      <table className="w-full text-xs">
                                        <thead>
                                          <tr className="text-left text-muted-foreground">
                                            <th className="pb-1 font-medium">#</th>
                                            <th className="pb-1 font-medium">Exercise</th>
                                            <th className="pb-1 font-medium">Category</th>
                                            <th className="pb-1 font-medium">Details</th>
                                            <th className="pb-1 font-medium">Pack</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {workout.exercises.map(ex => (
                                            <tr key={ex.order} className="border-t border-border/30">
                                              <td className="py-1 text-muted-foreground">{ex.order}</td>
                                              <td className="py-1 font-medium">{ex.name}</td>
                                              <td className="py-1">
                                                <span className={`rounded px-1 py-0.5 ${
                                                  ex.category === 'Elevation' ? 'bg-emerald-50 text-emerald-700' :
                                                  ex.category === 'Strength' ? 'bg-red-50 text-red-700' :
                                                  ex.category === 'Cardio' ? 'bg-blue-50 text-blue-700' :
                                                  ex.category === 'HIIT' ? 'bg-orange-50 text-orange-700' :
                                                  'bg-gray-50 text-gray-700'
                                                }`}>
                                                  {ex.category}
                                                </span>
                                              </td>
                                              <td className="py-1 text-muted-foreground">
                                                {formatWorkoutExerciseDetails(ex, {
                                                  formatDistance: (km) => measurements.format(km, 'distance'),
                                                  formatElevation: (meters) => measurements.format(meters, 'elevation')
                                                }) || '—'}
                                              </td>
                                              <td className="py-1">
                                                {ex.usePack ? (
                                                  <span className="text-primary">Yes</span>
                                                ) : (
                                                  <span className="text-muted-foreground">No</span>
                                                )}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                      {workout.notes && (
                                        <p className="mt-2 text-xs italic text-muted-foreground">{workout.notes}</p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                          {week.coachNotes.length > 0 && (
                            <div className="mt-3 rounded-md border border-border/60 bg-background/70 px-3 py-2">
                              <div className="text-xs font-semibold text-muted-foreground">Coach Notes</div>
                              <div className="mt-2 space-y-2 text-xs">
                                {week.coachNotes.map((note, noteIndex) => {
                                  const noteDate = new Date(note.effectiveDate ?? note.createdAt)
                                  return (
                                    <div
                                      key={`${note.reason}-${noteIndex}`}
                                      className="rounded border border-border/40 bg-muted/30 px-2 py-1"
                                    >
                                      <div className="flex flex-wrap items-center justify-between gap-2">
                                        <span className="font-medium">{note.reason}</span>
                                        <span className="text-[10px] text-muted-foreground">
                                          {noteDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        </span>
                                      </div>
                                      <p className="mt-1 text-muted-foreground">{note.message}</p>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )}
                          {week.notes && (
                            <p className="mt-2 text-xs italic text-muted-foreground">{week.notes}</p>
                          )}
                        </div>
                      )}
                      </div>
                    )
                  })}
                </CardContent>
              </Card>

              <div className="flex justify-end border-t border-border/60 pt-4">
                <Button type="button" variant="outline" onClick={handleExportMarkdown}>
                  <Download className="mr-2 h-4 w-4" />
                  Export Markdown
                </Button>
              </div>
            </>
          )}

          {!result && !error && (
            <Card className="border-dashed">
              <CardContent className="flex min-h-[60vh] flex-col items-center justify-center py-16 text-center">
                <Dumbbell className="mb-4 h-12 w-12 text-muted-foreground/50" />
                <h3 className="text-lg font-medium text-foreground">No plan generated yet</h3>
                <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                  Configure the panels above, then hit{' '}
                  <span className="font-medium text-foreground">Generate Test Plan</span>. Results render
                  below — collapse the input panels to focus on them.
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        <aside className="min-w-0 xl:sticky xl:top-4 xl:max-h-[calc(100vh-2rem)] xl:overflow-y-auto">
          <CodifiedPlanRulesPane
            compact
            title="Rules Reference"
            description="The same generation and milestone rules shown on Plan Algorithm, kept next to test output for warning and trace review."
          />
        </aside>
      </div>
    </div>
  )
}
